import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useITeachConfig } from "./_context/PresetContext";
import { Modal, Button } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import ApiService from "./_services/ApiService";
import AiWorkspaceService from "./_services/AiWorkspaceService";
import Sidebar from "./components/Sidebar/Sidebar";
import TagMultiSelect from "./components/TagMultiSelect";
import TabNavigation from "./components/LMBuilder/TabNavigation";
import OverviewTab from "./components/LMBuilder/OverviewTab";
import BuilderTab from "./components/LMBuilder/BuilderTab";
import TableViewTab from "./components/LMBuilder/TableViewTab";
import FlipClassBoardTab from "./components/LMBuilder/FlipClassBoardTab";
import BatchesTab from "./components/LMBuilder/BatchesTab";
import IssueTab from "./components/LMBuilder/IssueTab";
import type { TabType, FlipClassPhaseConfig } from "./components/LMBuilder/types";
import QuizFormPanel from "./pages/quick-quiz-maker/QuizFormPanel";
import ExcelImportModal from "./components/LMBuilder/ExcelImportModal";
import AiGenerateModal, { type GenerationType, type AiGenerateModalContext, type ChatMessage as AiChatMessage, type AiQuizQuestion, type GenerationOptions } from "./components/LMBuilder/AiGenerateModal";
import ScormService, { type ScormCourse } from "./_services/scorm.service";

// ================= Schema (Backend Structure) =================
export type Tag = {
  id: number;
  name: string;
  displayLabel?: string;
  code?: string;
  typeId?: number;
  typeName?: string;
  typeCode?: string;
};

export type Resource = {
  id?: number;       // Database ID (present after resource is saved)
  name: string;
  resourceType: "studyResource" | "test";  // Resource type: study resource or test
  // Study Resource fields
  studyResourceTypeId?: number;
  studyResourceSubTypeId?: number;  // Subtype ID for the resource
  content?: string;
  fileUrl?: string;  // For file uploads or URLs
  url?: string;      // For direct URL inputs
  file?: File;       // Actual file object for upload
  fileKey?: string;  // Key for FormData reference
  // Test fields
  testId?: string;   // Test ID for test resources
  testName?: string; // Optional test name for display
  // Common fields
  sortOrder: number;
  tags?: Tag[];      // Array of selected tags
};

export type ResourceSubType = {
  id: number;
  name: string;
  studyResourceTypeId: number;
  enableStatus: number;
  originalSubTypeIdId: number;
  config: any;
};

export type ResourceType = {
  id: number;
  name: string;
  presignedAccess: number;
  enableStatus: number;
  config: any;
  threshold: any;
  subtypes: ResourceSubType[];
};

export type Step = {
  name: string;
  code: string;
  order: number;
  sortOrder: number;
  startDate: string | null;
  endDate: string | null;
  group: string;
  resources: Resource[];
};

export type Group = {
  index: number;
  name: string;
  color: string;
  steps: Step[];
};

export type ModuleMeta = {
  title: string;
  audience: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  durationMins: number;
  tone: "Formal" | "Friendly" | "Practical" | "Academic";
};

export type LearningModule = {
  id?: number;
  name: string;
  code: string;
  status: number;
  sortOrder: number;
  remarks: string;
  groups: Group[];
  lessonPlanType?: 'learning_module' | 'lesson_plan';
  lessonPlanSubTypeId?: number;
  selectedTopics?: number[];
  selectedBatches?: number[];
  analyticsDashboard?: number;
  icon?: string;
  iconFile?: File;
};

// UI display aliases (for readability in frontend)
export type Section = Step;
export type Chapter = Group;

// ================= URL Dedup Helpers =================

/** Extract YouTube video ID from various URL formats */
function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

const YouTubePreview: React.FC<{ url: string | undefined }> = ({ url }) => {
  const [debouncedUrl, setDebouncedUrl] = useState(url || "");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUrl(url || ""), 600);
    return () => clearTimeout(t);
  }, [url]);
  const id = extractYouTubeId(debouncedUrl);
  if (!id) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
        Preview — confirm the video plays here before saving. If you see "Video unavailable" or "Playback disabled", pick a different link.
      </div>
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 8, overflow: "hidden", background: "#000", maxWidth: 480 }}>
        <iframe
          key={id}
          src={`https://www.youtube.com/embed/${id}`}
          title="YouTube preview"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

/** Normalize a URL for dedup comparison — collapses YouTube variants to a canonical form, strips trailing slashes and protocol */
function normalizeUrlForDedup(url: string): string {
  const ytId = extractYouTubeId(url);
  if (ytId) return `youtube:${ytId}`;
  try {
    const u = new URL(url);
    // lowercase host, strip trailing slash, drop fragment
    return `${u.host.toLowerCase()}${u.pathname.replace(/\/+$/, "")}${u.search}`.toLowerCase();
  } catch {
    return url.trim().toLowerCase().replace(/\/+$/, "");
  }
}

/** Collect all normalized URLs from every resource in the module */
function collectExistingUrls(mod: LearningModule | null): Set<string> {
  const urls = new Set<string>();
  if (!mod) return urls;
  for (const group of mod.groups) {
    for (const step of group.steps) {
      for (const r of step.resources || []) {
        const link = r.url || r.fileUrl;
        if (link) urls.add(normalizeUrlForDedup(link));
      }
    }
  }
  return urls;
}

// ================= Embed/RAG Types =================
interface EmbedResource {
  studyResourceId: number;
  resourceName: string | null;
  resourceUrl: string | null;
  resourceType: string;
  fileSizeBytes: number | null;
  status: 'completed' | 'failed' | 'pending' | 'processing';
  isEmbedded: boolean;
  chunksCreated: number;
  vectorsStored: number;
  processingTimeMs: number;
  errorMessage: string | null;
  processedAt: string | null;
}

interface EmbedStatusData {
  lessonPlanId: number;
  status: 'completed' | 'partial' | 'failed' | 'pending' | 'processing';
  totalResources: number;
  processedResources: number;
  totalChunks: number;
  totalVectors: number;
  processingStartedAt: string | null;
  processingCompletedAt: string | null;
  errorSummary: string | null;
  resources: EmbedResource[];
}

interface EmbedStatusResponse {
  success: boolean;
  isRAGified: boolean;
  message?: string;
  data?: EmbedStatusData;
}

// ================= Utilities =================
const uid = () => Math.random().toString(36).slice(2, 9);
const move = <T,>(arr: T[], from: number, to: number) => {
  const copy = arr.slice();
  const [sp] = copy.splice(from, 1);
  copy.splice(to, 0, sp);
  return copy;
};
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const DEFAULT_META: ModuleMeta = {
  title: "New Learning Module",
  audience: "All Learners",
  difficulty: "Beginner",
  durationMins: 60,
  tone: "Practical",
};

// Configuration for required fields based on study resource type ID
const RESOURCE_TYPE_CONFIG: Record<number, {
  name: string;
  requiresName: boolean;
  requiresContent: boolean;
  requiresUrl: boolean;
  requiresFile: boolean;
  allowFileOrUrl: boolean; // Either file or URL is required
  attachFromLibrary?: boolean; // Picked from an existing library (no upload here)
}> = {
  1: { name: "Download", requiresName: true, requiresContent: false, requiresUrl: false, requiresFile: false, allowFileOrUrl: true },
  3: { name: "PPT", requiresName: true, requiresContent: false, requiresUrl: false, requiresFile: true, allowFileOrUrl: false },
  5: { name: "Reference", requiresName: true, requiresContent: false, requiresUrl: true, requiresFile: false, allowFileOrUrl: false },
  6: { name: "Announcement", requiresName: true, requiresContent: true, requiresUrl: false, requiresFile: false, allowFileOrUrl: false },
  13: { name: "E-Books", requiresName: true, requiresContent: false, requiresUrl: false, requiresFile: false, allowFileOrUrl: true },
  27: { name: "Scorm Course", requiresName: true, requiresContent: false, requiresUrl: false, requiresFile: false, allowFileOrUrl: false, attachFromLibrary: true },
  31: { name: "Videos", requiresName: true, requiresContent: false, requiresUrl: false, requiresFile: false, allowFileOrUrl: true },
  32: { name: "Youtube Videos", requiresName: true, requiresContent: false, requiresUrl: true, requiresFile: false, allowFileOrUrl: false },
  // Spin Wheel Game — native; wedge JSON lives in `content`, no URL/file needed.
  41: { name: "Spin Wheel Game", requiresName: true, requiresContent: true, requiresUrl: false, requiresFile: false, allowFileOrUrl: false },
};

// Helper function to get required fields for a study resource type
const getRequiredFields = (typeId: number) => {
  return RESOURCE_TYPE_CONFIG[typeId] || {
    name: "Unknown",
    requiresName: true,
    requiresContent: false,
    requiresUrl: false,
    requiresFile: false,
    allowFileOrUrl: false
  };
};

function defaultStep(sectionName = "New Section", groupName = "Topic 1", order = 1): Step {
  return {
    name: sectionName,
    code: slugify(sectionName).toUpperCase().replace(/-/g, "_"),
    order: order,
    sortOrder: order,
    startDate: null,
    endDate: null,
    group: groupName,
    resources: [],
  };
}

function downloadJSON(filename: string, data: any) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ================= Styles =================
const styles = `
  .lmb-wrap { margin: 20px 16px; padding: 0; font-family: system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, sans-serif; color: #0f172a; transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
  @media (min-width: 1024px) { .lmb-wrap { margin-left: 296px; } }
  @media (min-width: 1024px) { .lmb-wrap.sidebar-collapsed { margin-left: 96px; } }
  .lmb-menu-btn { display: none !important; }
  @media (max-width: 1023px) { .lmb-menu-btn { display: flex !important; } }
  .lmb-h1 { font-size: 22px; margin: 0 0 16px; font-weight: 600; }
  .lmb-tag { background: rgba(0,0,0,.9); color:#fff; padding:3px 8px; font-size: 11px; border-radius: 999px; margin-left: 8px; display: inline-block; }
  .lmb-row { display: flex; gap: 0; position: relative; }
  @media (max-width: 900px){ .lmb-row{ flex-direction: column; } }
  .lmb-builder-panel { flex: 1; min-width: 0; }
  .lmb-json-panel { position: fixed; right: 0; top: 0; bottom: 0; background: #fff; border-left: 2px solid #e5e7eb; box-shadow: -2px 0 8px rgba(0,0,0,.05); overflow: hidden; display: flex; flex-direction: column; z-index: 100; transition: transform 0.3s ease; }
  .lmb-json-panel.collapsed { transform: translateX(100%); }
  .lmb-resizer { position: absolute; left: 0; top: 0; bottom: 0; width: 6px; cursor: ew-resize; background: transparent; z-index: 10; }
  .lmb-resizer:hover { background: #3b82f6; }
  .lmb-resizer.dragging { background: #3b82f6; }
  .lmb-json-toggle { position: fixed; right: 20px; bottom: 20px; z-index: 99; background: var(--gradient-brand); color: white; border: none; border-radius: 50%; width: 56px; height: 56px; font-size: 24px; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,.15); display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease; }
  .lmb-json-toggle:hover { transform: scale(1.1); }
  .lmb-json-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
  .lmb-json-body { flex: 1; overflow: auto; padding: 16px 18px; }
  .lmb-card { background: #fff; border:1px solid #e5e7eb; border-radius: 16px; box-shadow: 0 1px 2px rgba(0,0,0,.04); overflow: hidden; margin-bottom: 16px; }
  .lmb-card-hd { display:flex; align-items:center; justify-content:space-between; gap: 8px; padding: 14px 18px; border-bottom:1px solid #e5e7eb; }
  .lmb-card-bd { padding: 16px 18px; }
  .lmb-muted { color: #6b7280; font-size: 13px; }
  .lmb-grid-3 { display:grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .lmb-grid-2 { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 700px){ .lmb-grid-3, .lmb-grid-2 { grid-template-columns: 1fr; } }
  .lmb-input, .lmb-select, .lmb-textarea { width:100%; padding:10px 12px; border:1px solid #e5e7eb; border-radius:12px; background:#fff; outline:none; font-family: inherit; font-size: 14px; }
  .lmb-input:focus, .lmb-select:focus, .lmb-textarea:focus { border-color:#000; }
  .lmb-textarea { min-height: 110px; resize: vertical; }
  .lmb-btn { border:1px solid #e5e7eb; background:#fff; padding:8px 12px; border-radius: 14px; cursor:pointer; font-size: 14px; font-family: inherit; }
  .lmb-btn:hover { background:#f2f4f7; }
  .lmb-btn-primary { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color:#fff; border: none; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3); transition: all 0.15s; }
  .lmb-btn-primary:hover { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); transform: translateY(-1px); box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4); }
  .lmb-btn-primary:active { transform: translateY(0); box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3); }
  .lmb-btn-sm { padding:5px 8px; font-size: 12px; }
  .lmb-stack { display:flex; flex-wrap: wrap; gap:8px; align-items:center; }
  .lmb-group { border:1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-top: 10px; background: #fff; }
  .lmb-hr { height:1px; background: #e5e7eb; margin: 10px 0; }

  /* Collapsible styles */
  .lmb-chapter-header { cursor: pointer; user-select: none; display: flex; align-items: center; gap: 10px; padding: 16px 18px; background: linear-gradient(to right, #f9fafb, #ffffff); border-radius: 12px; border: 2px solid #e5e7eb; margin-bottom: 16px; margin-top: 24px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); transition: all 0.2s ease; }
  .lmb-chapter-header:hover { background: linear-gradient(to right, #f3f4f6, #f9fafb); box-shadow: 0 4px 8px rgba(0,0,0,0.08); }
  .lmb-chapter-toggle { font-size: 18px; transition: transform 0.2s ease; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; color: #4b5563; }
  .lmb-chapter-toggle.expanded { transform: rotate(90deg); }
  .lmb-chapter-title { flex: 1; font-weight: 700; font-size: 16px; color: #111827; }
  .lmb-chapter-badge { background: #e5e7eb; color: #374151; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
  .lmb-chapter-content { margin-left: 20px; padding: 16px; background: rgba(249, 250, 251, 0.5); border-left: 4px solid; border-radius: 0 8px 8px 0; margin-bottom: 12px; }

  .lmb-section-header { cursor: pointer; user-select: none; display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #ffffff; border: 1px solid #d1d5db; border-left: 3px solid; border-radius: 8px; margin-top: 12px; margin-bottom: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; }
  .lmb-section-header:hover { background: #f9fafb; box-shadow: 0 2px 4px rgba(0,0,0,0.08); }
  .lmb-section-toggle { font-size: 14px; transition: transform 0.2s ease; display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; color: #6b7280; }
  .lmb-section-toggle.expanded { transform: rotate(90deg); }
  .lmb-section-title { flex: 1; font-weight: 600; font-size: 14px; color: #374151; }
  .lmb-section-badge { background: #f3f4f6; color: #6b7280; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; }
  .lmb-section-content { margin-left: 40px; padding-left: 16px; border-left: 2px dashed #e5e7eb; margin-top: 8px; }
  .lmb-resource-item { background: #ffffff; border: 1px solid #e5e7eb; border-left: 3px solid #a0aec0; border-radius: 8px; padding: 14px; margin-top: 10px; margin-left: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
  .lmb-btn-add { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; border: none; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3); display: inline-flex; align-items: center; gap: 6px; }
  .lmb-btn-add:hover { background: linear-gradient(135deg, #059669 0%, #047857 100%); transform: translateY(-1px); box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4); }
  .lmb-btn-add:active { transform: translateY(0); box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3); }
  .lmb-space-between { display:flex; align-items:center; justify-content: space-between; gap:8px; margin-bottom: 8px; }
  .lmb-json { background:#f6f7fb; border:1px solid #e5e7eb; border-radius: 12px; padding: 12px; overflow:auto; max-height: 680px; font:12px/1.5 ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace; white-space: pre; }
  .lmb-empty { color: #6b7280; border:1px dashed #e5e7eb; border-radius: 12px; padding: 16px; text-align:center; }
  .lmb-label { display: block; margin-bottom: 6px; }
  .lmb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .lmb-prompt-hint { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 10px; margin-bottom: 12px; font-size: 13px; color: #0c4a6e; }
  .lmb-loading { display: inline-block; width: 14px; height: 14px; border: 2px solid #fff; border-radius: 50%; border-top-color: transparent; animation: lmb-spin 0.6s linear infinite; }
  @keyframes lmb-spin { to { transform: rotate(360deg); } }

  /* Tag Multi-Select Styles */
  .tag-multi-select { position: relative; width: 100%; }
  .tag-multi-select-container { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; min-height: 42px; cursor: pointer; }
  .tag-multi-select-container:focus-within { border-color: #000; }
  .tag-chips-container { flex: 1; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .tag-placeholder { color: #9ca3af; font-size: 14px; }
  .tag-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: var(--gradient-brand); color: #fff; border-radius: 6px; font-size: 12px; font-weight: 500; }
  .tag-chip-remove { background: none; border: none; color: #fff; font-size: 16px; line-height: 1; cursor: pointer; padding: 0 2px; margin-left: 2px; }
  .tag-chip-remove:hover { opacity: 0.8; }
  .tag-clear-all { background: none; border: none; color: #6b7280; font-size: 11px; cursor: pointer; padding: 4px 8px; border-radius: 6px; transition: all 0.15s; white-space: nowrap; }
  .tag-clear-all:hover { background: #f3f4f6; color: #374151; }
  .tag-dropdown-toggle { background: none; border: none; padding: 4px; font-size: 12px; color: #6b7280; cursor: pointer; transition: transform 0.2s; }
  .tag-dropdown-toggle:hover { color: #000; }
  .tag-dropdown-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,.1); z-index: 100; max-height: 300px; display: flex; flex-direction: column; }
  .tag-search-container { padding: 12px; border-bottom: 1px solid #e5e7eb; }
  .tag-search-input { width: 100%; padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; outline: none; }
  .tag-search-input:focus { border-color: #667eea; }
  .tag-actions { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; }
  .tag-action-btn { background: none; border: none; color: #667eea; font-size: 13px; cursor: pointer; padding: 4px 8px; border-radius: 6px; }
  .tag-action-btn:hover { background: #f3f4f6; }
  .tag-list { overflow-y: auto; max-height: 200px; }
  .tag-list-item { display: flex; align-items: center; gap: 8px; padding: 10px 12px; cursor: pointer; transition: background 0.15s; }
  .tag-list-item:hover { background: #f9fafb; }
  .tag-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: #667eea; }
  .tag-label { flex: 1; font-size: 14px; color: #0f172a; }
  .tag-code { color: #6b7280; font-size: 12px; }
  .tag-list-empty { padding: 20px; text-align: center; color: #9ca3af; font-size: 14px; }

  /* Quiz Creation Modal - override global .modal-content leaks */
  .modal-dialog.lmb-quiz-creation-modal,
  .modal-dialog.lmb-quiz-creation-modal.modal-fullscreen {
    max-width: 100vw !important;
    width: 100vw !important;
    height: 100vh !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .modal-dialog.lmb-quiz-creation-modal .modal-content,
  .modal-dialog.lmb-quiz-creation-modal.modal-fullscreen .modal-content {
    max-width: 100% !important;
    width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
    border: none !important;
  }

  /* Save Result Modal Styles */
  .save-result-modal .modal-dialog { max-width: 420px !important; }
  .save-result-modal .modal-content { width: 100% !important; border-radius: 12px; }

  /* Tab Navigation Styles */
  .lmb-tab-navigation { margin-bottom: 20px; }
  .lmb-tab-list { display: flex; gap: 4px; border-bottom: 2px solid #e5e7eb; }
  .lmb-tab-button { background: none; border: none; padding: 12px 20px; font-size: 14px; font-weight: 500; color: #6b7280; cursor: pointer; display: flex; align-items: center; gap: 8px; border-bottom: 3px solid transparent; transition: all 0.2s ease; position: relative; }
  .lmb-tab-button:hover { color: #000; background: #f9fafb; }
  .lmb-tab-button.active { color: #000; border-bottom-color: #667eea; font-weight: 600; }
  .lmb-tab-icon { font-size: 18px; }
  .lmb-tab-label { font-size: 14px; }
  .lmb-tab-badge { width: 8px; height: 8px; background: #ef4444; border-radius: 50%; margin-left: 4px; }
  @media (max-width: 700px) { .lmb-tab-button { padding: 10px 12px; font-size: 13px; } .lmb-tab-icon { font-size: 16px; } .lmb-tab-label { display: none; } }

  /* Overview Tab Styles */
  .lmb-overview-tab { }
  .lmb-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .lmb-stat-item { display: flex; align-items: center; gap: 12px; padding: 12px; background: #f9fafb; border-radius: 12px; }
  .lmb-stat-icon { font-size: 24px; }
  .lmb-stat-content { flex: 1; }
  .lmb-stat-value { font-size: 22px; font-weight: 700; color: #0f172a; line-height: 1.2; }
  .lmb-stat-label { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .lmb-stat-details { }
  .lmb-stat-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
  .lmb-stat-row:last-child { border-bottom: none; }
  .lmb-status-badge { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
  .lmb-status-badge.active { background: #d1fae5; color: #065f46; }
  .lmb-status-badge.inactive { background: #fee2e2; color: #991b1b; }
  @media (max-width: 700px) { .lmb-stats-grid { grid-template-columns: 1fr; } }

  /* Builder Tab Styles */
  .lmb-builder-tab { }

  /* Table View Styles */
  .lmb-table-view-tab { }
  .lmb-bulk-operations { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 12px; }
  .lmb-bulk-left { flex: 1; }
  .lmb-bulk-right { display: flex; gap: 8px; }
  .lmb-filter-input { min-width: 0; max-width: 300px; }
  .lmb-table-container { overflow-x: auto; border-radius: 12px; border: 1px solid #e5e7eb; }
  .lmb-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .lmb-table thead th { background: #f9fafb; padding: 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; white-space: nowrap; }
  .lmb-table tbody tr { border-bottom: 1px solid #f3f4f6; }
  .lmb-table tbody tr:last-child { border-bottom: none; }
  .lmb-table tbody td { padding: 10px 12px; }
  .lmb-table-chapter-row { background: #f9fafb; font-weight: 600; }
  .lmb-table-chapter-row td { padding: 12px; }
  .lmb-table-section-row:hover { background: #fafbfc; }
  .lmb-table-toggle { background: none; border: none; cursor: pointer; padding: 4px; font-size: 12px; color: #6b7280; transition: color 0.2s; }
  .lmb-table-toggle:hover { color: #000; }
  .lmb-table-input { width: 100%; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 8px; font-size: 13px; font-family: inherit; }
  .lmb-table-input:focus { outline: none; border-color: #667eea; }
  .lmb-table-center { text-align: center; }
  .lmb-resource-count { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: #e0e7ff; color: #4338ca; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: help; }
  .lmb-table-action-btn { background: none; border: none; cursor: pointer; padding: 4px 8px; font-size: 16px; border-radius: 6px; transition: background 0.2s; }
  .lmb-table-action-btn:hover { background: #fee2e2; }
  @media (max-width: 900px) { .lmb-bulk-operations { flex-direction: column; align-items: stretch; } .lmb-filter-input { max-width: none; } }

  /* Modal Styles */
  .lmb-modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
  .lmb-modal { background: white; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 500px; width: 90%; max-height: 90vh; display: flex; flex-direction: column; }
  .lmb-modal-large { max-width: 700px; }
  .lmb-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #e5e7eb; }
  .lmb-modal-header h3 { margin: 0; font-size: 18px; font-weight: 600; }
  .lmb-modal-close { background: none; border: none; font-size: 28px; line-height: 1; cursor: pointer; color: #6b7280; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; }
  .lmb-modal-close:hover { background: #f3f4f6; }
  .lmb-modal-body { padding: 20px; overflow-y: auto; }
  .lmb-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid #e5e7eb; }

  /* Resource Modal Styles */
  .lmb-resource-list { display: flex; flex-direction: column; gap: 12px; }
  .lmb-resource-modal-item { border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; background: #fafbfc; transition: all 0.2s; }
  .lmb-resource-modal-item:hover { border-color: #667eea; box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1); }
  .lmb-resource-modal-header { display: flex; align-items: flex-start; gap: 12px; }
  .lmb-resource-modal-number { flex-shrink: 0; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; background: #667eea; color: white; border-radius: 50%; font-weight: 600; font-size: 13px; }
  .lmb-resource-modal-content { flex: 1; min-width: 0; }
  .lmb-resource-modal-name { font-weight: 600; font-size: 15px; color: #0f172a; margin-bottom: 6px; }
  .lmb-resource-modal-name-input { font-weight: 600; font-size: 15px; margin-bottom: 6px; border: 1px solid #e5e7eb; padding: 6px 10px; }
  .lmb-resource-modal-meta { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 12px; color: #6b7280; }
  .lmb-resource-modal-type { padding: 3px 8px; background: #e0e7ff; color: #4338ca; border-radius: 6px; font-weight: 500; }
  .lmb-resource-modal-url a { color: #667eea; text-decoration: none; }
  .lmb-resource-modal-url a:hover { text-decoration: underline; }
  .lmb-resource-modal-file { color: #059669; }
  .lmb-resource-modal-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .lmb-resource-tag { padding: 4px 10px; background: var(--gradient-brand); color: white; border-radius: 6px; font-size: 11px; font-weight: 600; }
  .lmb-resource-modal-actions { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }
  .lmb-resource-modal-toggle { background: #f3f4f6; border: 1px solid #e5e7eb; cursor: pointer; padding: 6px 10px; font-size: 14px; border-radius: 6px; transition: all 0.2s; }
  .lmb-resource-modal-toggle:hover { background: #e5e7eb; }
  .lmb-resource-modal-remove { background: none; border: none; cursor: pointer; padding: 6px 10px; font-size: 18px; border-radius: 6px; transition: background 0.2s; }
  .lmb-resource-modal-remove:hover { background: #fee2e2; }
  .lmb-resource-modal-expanded { margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb; display: flex; flex-direction: column; gap: 12px; }
  .lmb-resource-modal-field { display: flex; flex-direction: column; gap: 4px; }

  /* Side Panel Styles */
  .lmb-side-panel-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.2); z-index: 998; opacity: 0; pointer-events: none; transition: opacity 0.3s ease; }
  .lmb-side-panel-overlay.visible { opacity: 1; pointer-events: auto; }
  .lmb-side-panel { position: fixed; top: 0; right: 0; height: 100vh; width: 420px; max-width: 90vw; background: white; box-shadow: -4px 0 15px rgba(0, 0, 0, 0.1); transform: translateX(100%); transition: transform 0.3s ease; z-index: 999; display: flex; flex-direction: column; }
  .lmb-side-panel.open { transform: translateX(0); }
  .lmb-side-panel-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px; border-bottom: 1px solid #e5e7eb; background: #fafbfc; }
  .lmb-side-panel-title { flex: 1; min-width: 0; }
  .lmb-side-panel-title h3 { margin: 0; font-size: 18px; font-weight: 600; color: #0f172a; word-break: break-word; }
  .lmb-side-panel-breadcrumb { font-size: 12px; color: #6b7280; margin-bottom: 6px; }
  .lmb-side-panel-close { background: none; border: none; font-size: 28px; line-height: 1; cursor: pointer; color: #6b7280; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; flex-shrink: 0; margin-left: 12px; }
  .lmb-side-panel-close:hover { background: #f3f4f6; }
  .lmb-side-panel-body { flex: 1; overflow-y: auto; padding: 20px; }
  .lmb-side-panel-section { margin-bottom: 20px; }
  .lmb-side-panel-section:last-child { margin-bottom: 0; }
  .lmb-side-panel-label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
  .lmb-side-panel-value { font-size: 15px; color: #0f172a; padding: 8px 12px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb; }
  .lmb-side-panel-link { color: #667eea; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
  .lmb-side-panel-link:hover { text-decoration: underline; }
  .lmb-side-panel-link::before { content: "🔗"; }
  .lmb-side-panel-file { font-size: 13px; color: #059669; word-break: break-all; }
  .lmb-side-panel-content { font-size: 14px; line-height: 1.7; color: #374151; padding: 16px; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb; white-space: pre-wrap; max-height: 400px; overflow-y: auto; }
  .lmb-side-panel-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .lmb-side-panel-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid #e5e7eb; background: #fafbfc; }
  .lmb-side-panel-debug { margin-top: 20px; padding-top: 16px; border-top: 1px dashed #e5e7eb; opacity: 0.7; }
  .lmb-side-panel-debug:hover { opacity: 1; }
  .lmb-side-panel-id-link { color: #6b7280; text-decoration: none; font-family: monospace; font-size: 13px; }
  .lmb-side-panel-id-link:hover { color: #667eea; text-decoration: underline; }

  /* Resource Count Button in Table */
  .lmb-resource-count-btn { cursor: pointer; border: 1px solid #4338ca; transition: all 0.2s; }
  .lmb-resource-count-btn:hover { background: #4338ca; color: white; transform: translateY(-1px); box-shadow: 0 2px 6px rgba(67, 56, 202, 0.3); }

  /* ============= Board View Styles ============= */

  /* Board Layout */
  .lmb-board-view {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    background: #f9fafb;
    min-height: 600px;
  }

  .lmb-board-columns {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;
  }

  /* Column Styles */
  .lmb-board-column {
    background: #ffffff;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 500px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border-top: 4px solid #e5e7eb;
  }

  /* Phase-specific column colors */
  .pre-class-column {
    border-top-color: #FF9800;
  }

  .in-class-column {
    border-top-color: #4CAF50;
  }

  .post-class-column {
    border-top-color: #2196F3;
  }

  .lmb-column-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 2px solid #e5e7eb;
  }

  .lmb-column-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .lmb-column-count {
    background: #e5e7eb;
    color: #374151;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }

  .lmb-column-dropzone {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px;
    border-radius: 8px;
    transition: background 0.2s;
    min-height: 200px;
  }

  .lmb-column-dropzone.dragging-over {
    background: rgba(102, 126, 234, 0.1);
    border: 2px dashed #667eea;
  }

  .lmb-empty-column {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 14px;
    font-style: italic;
  }

  /* Resource Card */
  .lmb-resource-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: grab;
    transition: all 0.2s;
  }

  .lmb-resource-card:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
  }

  .lmb-resource-card.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .lmb-resource-icon {
    font-size: 24px;
    flex-shrink: 0;
  }

  .lmb-resource-content {
    flex: 1;
    min-width: 0;
  }

  .lmb-resource-name {
    font-weight: 600;
    font-size: 14px;
    color: #0f172a;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lmb-resource-breadcrumb {
    font-size: 12px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lmb-resource-actions {
    display: flex;
    gap: 4px;
  }

  .lmb-resource-actions button {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.2s;
  }

  .lmb-resource-actions button:hover {
    background: #f3f4f6;
  }

  /* Add Resource Button */
  .lmb-add-resource-btn {
    width: 100%;
    padding: 10px;
    background: white;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .lmb-add-resource-btn:hover {
    border-color: #667eea;
    color: #667eea;
    background: rgba(102, 126, 234, 0.05);
  }

  /* Untagged Section */
  .lmb-untagged-section {
    background: white;
    border: 1px dashed #d1d5db;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .lmb-untagged-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .lmb-untagged-header h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #6b7280;
  }

  .lmb-untagged-count {
    background: #f3f4f6;
    color: #6b7280;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }

  .lmb-untagged-list {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 8px 0;
  }

  .lmb-untagged-list .lmb-resource-card {
    flex-shrink: 0;
    min-width: 280px;
  }

  .lmb-empty-untagged {
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
    padding: 30px;
  }

  .lmb-untagged-list.dragging-over {
    background: rgba(156, 163, 175, 0.1);
    border-radius: 8px;
  }

  /* Matrix/Resources View Styles */
  .lmb-resources-matrix-view {
    padding: 16px;
    background: #f9fafb;
    min-height: calc(100vh - 60px);
  }

  .lmb-matrix-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow-x: hidden;
    overflow-y: auto;
    max-height: calc(100vh - 100px);
  }

  .lmb-matrix-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  .lmb-matrix-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f9fafb;
  }

  .lmb-matrix-step-header {
    background: #f9fafb;
    padding: 12px;
    text-align: left;
    font-weight: 700;
    font-size: 13px;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
    position: sticky;
    left: 0;
    z-index: 11;
    width: 15%;
    max-width: 200px;
  }

  .lmb-matrix-phase-header {
    background: #f9fafb;
    padding: 12px 8px;
    text-align: center;
    font-weight: 700;
    font-size: 12px;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
    width: auto;
  }

  .lmb-matrix-phase-header:last-child {
    border-right: none;
  }

  .lmb-matrix-untagged-header {
    background: #fef3c7;
  }

  .lmb-matrix-row {
    transition: background 0.2s;
  }

  .lmb-matrix-row:hover {
    background: #f9fafb;
  }

  .lmb-matrix-step-cell {
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
    background: white;
    position: sticky;
    left: 0;
    z-index: 5;
    width: 18%;
    max-width: 260px;
  }

  .lmb-step-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .lmb-matrix-step-cell .lmb-chapter-badge {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    color: white;
    width: fit-content;
    max-width: 100%;
    overflow: hidden;
    word-break: break-word;
    line-height: 1.3;
  }

  .lmb-step-name {
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    line-height: 1.3;
  }

  .lmb-matrix-cell {
    padding: 8px;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
    background: white;
    vertical-align: top;
    width: auto;
  }

  .lmb-matrix-cell:last-child {
    border-right: none;
  }

  .lmb-matrix-untagged-cell {
    background: #fffbeb;
  }

  .lmb-cell-dropzone {
    min-height: 60px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px;
    border-radius: 6px;
    transition: background 0.2s;
  }

  .lmb-cell-dropzone.dragging-over {
    background: rgba(102, 126, 234, 0.1);
    border: 2px dashed #667eea;
  }

  .lmb-matrix-resource-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: grab;
    transition: all 0.2s;
  }

  .lmb-matrix-resource-card:hover {
    border-color: #667eea;
    box-shadow: 0 2px 6px rgba(102, 126, 234, 0.15);
    transform: translateY(-1px);
  }

  .lmb-matrix-resource-card.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .lmb-matrix-resource-card .lmb-resource-icon {
    font-size: 16px;
    flex-shrink: 0;
    line-height: 1;
  }

  .lmb-matrix-resource-card .lmb-resource-name {
    flex: 1;
    font-weight: 500;
    font-size: 12px;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.3;
  }

  .lmb-matrix-resource-card .lmb-resource-actions {
    display: flex;
    gap: 2px;
  }

  .lmb-matrix-resource-card .lmb-resource-actions button {
    background: none;
    border: none;
    font-size: 13px;
    cursor: pointer;
    padding: 3px;
    border-radius: 3px;
    transition: background 0.2s;
    line-height: 1;
  }

  .lmb-matrix-resource-card .lmb-resource-actions button:hover {
    background: #e5e7eb;
  }

  .lmb-ai-config-badge {
    color: #7c3aed;
    font-size: 10px;
    flex-shrink: 0;
    line-height: 1;
  }

  .lmb-empty-cell {
    text-align: center;
    color: #9ca3af;
    font-size: 11px;
    padding: 15px 4px;
    font-style: italic;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .lmb-board-columns {
      grid-template-columns: 1fr;
    }

    .lmb-untagged-list {
      flex-direction: column;
    }

    .lmb-untagged-list .lmb-resource-card {
      min-width: unset;
      width: 100%;
    }

    .lmb-matrix-container {
      overflow-x: auto;
    }

    .lmb-matrix-step-header,
    .lmb-matrix-step-cell {
      min-width: 240px;
    }

    .lmb-matrix-phase-header,
    .lmb-matrix-cell {
      min-width: 150px;
    }
  }
`;

export default function LearningModuleBuilderV2() {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { config: iteachConfig } = useITeachConfig();
  const lmConfig = iteachConfig.lmBuilder;

  // Check if we're in view mode (read-only)
  const queryParams = new URLSearchParams(window.location.search);
  const isViewMode = queryParams.get('mode') === 'view';

  const [mod, setMod] = useState<LearningModule | null>(() => ({
    name: DEFAULT_META.title,
    code: slugify(DEFAULT_META.title).toUpperCase().replace(/-/g, "_"),
    status: 1,
    sortOrder: 1,
    remarks: "",
    groups: [],
    lessonPlanType: lmConfig?.lessonPlanTypeRestrictTo || 'learning_module',
    flipClassEnabled: lmConfig?.flipClassEnabled ?? 0,
    analyticsDashboard: 1
  }));
  const [meta, setMeta] = useState<ModuleMeta>(DEFAULT_META);
  const [prompt, setPrompt] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showAIHelper, setShowAIHelper] = useState<boolean>(false);
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [loadingResourceTypes, setLoadingResourceTypes] = useState<boolean>(true);
  // SCORM library for attach-by-reference (type 27) — READY courses only
  const [scormLibrary, setScormLibrary] = useState<ScormCourse[]>([]);
  const [scormLibraryLoaded, setScormLibraryLoaded] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );
  useEffect(() => {
    const handler = () => setSidebarCollapsed(localStorage.getItem('sidebar-collapsed') === 'true');
    window.addEventListener('sidebar-toggled', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('sidebar-toggled', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  const [jsonPanelWidth, setJsonPanelWidth] = useState<number>(350);
  const [isJsonCollapsed, setIsJsonCollapsed] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState<boolean>(true);
  const [loadingModule, setLoadingModule] = useState<boolean>(false);
  const [flipClassPhaseTagIds, setFlipClassPhaseTagIds] = useState<number[]>([]);
  const [flipClassPhaseConfigs, setFlipClassPhaseConfigs] = useState<FlipClassPhaseConfig[]>([]);

  // Apply config defaults if config loads after initial state (async safety net)
  useEffect(() => {
    if (lmConfig && !id) {
      setMod(prev => prev ? {
        ...prev,
        ...(lmConfig.lessonPlanTypeRestrictTo && { lessonPlanType: lmConfig.lessonPlanTypeRestrictTo }),
        ...(lmConfig.flipClassEnabled !== undefined && { flipClassEnabled: lmConfig.flipClassEnabled }),
      } : prev);
    }
  }, [lmConfig]);

  // Quiz creation modal state (tracks which resource the modal is for: "ci-si-ri")
  const [quizModalKey, setQuizModalKey] = useState<string | null>(null);

  // AI helper states
  const [isAnalyzingModule, setIsAnalyzingModule] = useState<boolean>(false);
  const [aiLoadingStates, setAiLoadingStates] = useState<Record<string, boolean>>({});
  const [batchQuizProgress, setBatchQuizProgress] = useState<{
    phase: string; total: number; current: number;
    created: number; skipped: number; failed: number;
    currentStepName: string; done: boolean;
  } | null>(null);
  // AI generation feedback modal
  const [showAiModal, setShowAiModal] = useState<boolean>(false);
  const [aiModalMessage, setAiModalMessage] = useState<string>('');
  const [aiModalType, setAiModalType] = useState<'success' | 'error'>('success');

  const [aiConfiguredResourceIds, setAiConfiguredResourceIds] = useState<Set<number>>(new Set());

  // AI Generate modal state (intercept modal with choice + guided chat)
  const [aiGenerateModal, setAiGenerateModal] = useState<{
    isOpen: boolean;
    type: GenerationType;
    context: AiGenerateModalContext;
    chapterIndex?: number;
    sectionIndex?: number;
    resourceIndex?: number;
    phaseName?: string;
    phaseTags?: Tag[];
    generatedResources?: Resource[];
    generatedQuestions?: AiQuizQuestion[];
    isGenerating?: boolean;
    generationError?: string | null;
    quizBloomsEnabled?: boolean;
  }>({ isOpen: false, type: "content", context: {} });

  // Collapsible state: tracks which chapters/sections are expanded
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Tab state management
  // Create mode (no id): default to 'overview'
  // Edit/View mode (has id): default to 'resources'
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    return id ? 'resources' : 'overview';
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // Save state management
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [saveModalMessage, setSaveModalMessage] = useState<string>('');
  const [saveModalType, setSaveModalType] = useState<'success' | 'error'>('success');
  const [saveModalData, setSaveModalData] = useState<{
    title: string;
    chapters: number;
    sections: number;
    resources: number;
    resourceBreakdown: Array<{ icon: string; label: string; value: number }>;
  } | null>(null);
  const [savedModuleId, setSavedModuleId] = useState<number | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
  const [showExcelImport, setShowExcelImport] = useState<boolean>(false);

  // RAG Embed/Status/Query state (view mode actions)
  const [isModuleRagified, setIsModuleRagified] = useState(false);
  const [embeddingInProgress, setEmbeddingInProgress] = useState(false);
  const [showRAGModal, setShowRAGModal] = useState(false);
  const [ragModalTab, setRagModalTab] = useState<'status' | 'search'>('status');
  const [embedStatusLoading, setEmbedStatusLoading] = useState(false);
  const [embedStatusError, setEmbedStatusError] = useState<string | null>(null);
  const [embedStatus, setEmbedStatus] = useState<EmbedStatusResponse | null>(null);
  const [queryPrompt, setQueryPrompt] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResponse, setQueryResponse] = useState('');
  const [queryError, setQueryError] = useState<string | null>(null);
  // Embed/Status result feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<React.ReactNode>('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  // Status toggle (enable/disable) in view mode
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);

  const jsonPreview = useMemo(() => (mod ? JSON.stringify(mod, null, 2) : ""), [mod]);

  // Filter tags to only show flip-class phase tags
  const flipClassPhaseTags = useMemo(() => {
    if (flipClassPhaseTagIds.length === 0) return availableTags;
    const filtered = availableTags.filter(tag => flipClassPhaseTagIds.includes(tag.id));
    console.log('[LM Builder] Filtered flip-class phase tags:', filtered.length, 'out of', availableTags.length);
    return filtered;
  }, [availableTags, flipClassPhaseTagIds]);

  // Fetch SCORM library on mount (for the type-27 attach-from-library picker)
  useEffect(() => {
    ScormService.listCourses()
      .then((res) => setScormLibrary((res.data || []).filter((c: ScormCourse) => c.importStatus === 'READY')))
      .catch((err) => console.error("Failed to load SCORM library:", err))
      .finally(() => setScormLibraryLoaded(true));
  }, []);

  // Fetch resource types on mount
  useEffect(() => {
    const fetchResourceTypes = async () => {
      try {
        const response = await ApiService.getLearningResourceType();
        // Master table returns Id order — present alphabetically
        setResourceTypes(
          [...(response.data || [])].sort((a: ResourceType, b: ResourceType) =>
            (a.name || "").localeCompare(b.name || "")
          )
        );
      } catch (error) {
        console.error("Error fetching resource types:", error);
        alert("Failed to load resource types. Using default values.");
      } finally {
        setLoadingResourceTypes(false);
      }
    };
    fetchResourceTypes();
  }, []);

  // Fetch flip-class config to get valid phase tag IDs
  useEffect(() => {
    const fetchFlipClassConfig = async () => {
      try {
        const response = await ApiService.fetchFlipClassConfig();
        const configs = (response.data || []) as any[];
        if (configs.length > 0) {
          // Store full phase configs (sorted) for the review modal
          const sorted = [...configs].sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
          setFlipClassPhaseConfigs(sorted as FlipClassPhaseConfig[]);

          // Extract all tag IDs from all phase configs
          const tagIds: number[] = [];
          configs.forEach((config: any) => {
            if (config.tags && Array.isArray(config.tags)) {
              tagIds.push(...config.tags);
            }
          });
          const uniqueTagIds = Array.from(new Set(tagIds));
          console.log('[LM Builder] Flip-class phase tag IDs:', uniqueTagIds);
          setFlipClassPhaseTagIds(uniqueTagIds);
        }
      } catch (error) {
        console.error("[LM Builder] Error fetching flip-class config:", error);
      }
    };
    fetchFlipClassConfig();
  }, []);

  // Fetch tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        console.log('[LM Builder] Fetching tags...');
        const response = await ApiService.getAllTags({ enabled: true });
        const tags = (response.data || []) as Tag[];
        console.log('[LM Builder] Tags fetched:', tags.length, 'tags');
        console.log('[LM Builder] Sample tag:', tags[0]);
        console.log('[LM Builder] Tag types:', Array.from(new Set(tags.map(t => t.typeCode))));
        setAvailableTags(tags);
      } catch (error) {
        console.error("[LM Builder] Error fetching tags:", error);
        // Don't alert - tags are optional, silently fail
      } finally {
        console.log('[LM Builder] Setting loadingTags to false');
        setLoadingTags(false);
      }
    };
    fetchTags();
  }, []);

  // Load module from URL param on mount
  useEffect(() => {
    if (id) {
      const loadModule = async () => {
        try {
          setLoadingModule(true);
          const response = await ApiService.getLessonPlanDetails(Number(id));
          const loadedModule = response.data as LearningModule;

          setMod(loadedModule);

          if (loadedModule.name) {
            setMeta({
              ...meta,
              title: loadedModule.name,
            });
          }

          // Load AI config status for all study resources
          loadAiConfigStatus(loadedModule);

          // Check if module is RAGified (for AI quiz batch buttons)
          ApiService.getEmbededStatus(Number(id)).then((res: any) => {
            setIsModuleRagified(res.data?.isRAGified === true);
          }).catch(() => setIsModuleRagified(false));

          console.log("Module loaded successfully:", loadedModule);
          setInitialLoadComplete(true);
        } catch (error: any) {
          console.error("Error loading module:", error);
          alert("Failed to load module: " + (error.message || "Unknown error"));
          // Navigate back to list on error
          history.push('/learning-modules');
        } finally {
          setLoadingModule(false);
        }
      };
      loadModule();
    } else {
      // For new modules, mark initial load as complete
      setInitialLoadComplete(true);
    }
  }, [id]);


  // Track changes for unsaved indicator
  // Use a ref to skip the first render cycle after initialLoadComplete becomes true,
  // so that the initial API-loaded state doesn't falsely mark as "unsaved"
  const initialLoadSnapshotRef = useRef(false);
  const batchQuizRunningRef = useRef(false);
  const savingRef = useRef(false);
  useEffect(() => {
    if (!initialLoadComplete) return;
    // Skip the very first trigger after load completes (that's the loaded data, not a user change)
    if (!initialLoadSnapshotRef.current) {
      initialLoadSnapshotRef.current = true;
      return;
    }
    setHasUnsavedChanges(true);
  }, [mod, meta, initialLoadComplete]);

  // Tab change handler with confirmation if unsaved changes
  const handleTabChange = (newTab: TabType) => {
    if (hasUnsavedChanges && activeTab !== newTab) {
      // Could add confirmation dialog here if needed
      // For now, just switch tabs
    }
    setActiveTab(newTab);
  };

  // Handle resizing of JSON panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const windowWidth = window.innerWidth;
      const newWidth = Math.max(250, Math.min(800, windowWidth - e.clientX - 20));
      setJsonPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  const newEmptyModule = () => {
    setMod({
      name: meta.title,
      code: slugify(meta.title).toUpperCase().replace(/-/g, "_"),
      status: 1,
      sortOrder: 1,
      remarks: "",
      groups: [],
      analyticsDashboard: 1
    });
  };

  // Refetch module data from database
  const refetchModuleData = async (moduleId: number) => {
    try {
      console.log('[refetchModuleData] Fetching fresh data for module:', moduleId);
      const response = await ApiService.getLessonPlanDetails(moduleId);
      const freshModule = response.data as LearningModule;

      setMod(freshModule);
      console.log('[refetchModuleData] Module data refreshed with resource IDs:', freshModule);

      // Update meta as well
      if (freshModule.name) {
        setMeta({
          ...meta,
          title: freshModule.name,
        });
      }
    } catch (error) {
      console.error('[refetchModuleData] Error refetching module data:', error);
    }
  };

  const exportJSON = () => {
    if (!mod) return alert("Construct the module first.");
    downloadJSON(`${slugify(mod.name || "learning-module")}.json`, mod);
  };

  const handleExcelImport = (importedGroups: Group[], importMode: 'replace' | 'append') => {
    if (!mod) return;

    if (importMode === 'replace') {
      setMod({ ...mod, groups: importedGroups });
    } else {
      const existingCount = mod.groups.length;
      const reindexed = importedGroups.map((g, i) => ({
        ...g,
        index: existingCount + i + 1,
      }));
      setMod({ ...mod, groups: [...mod.groups, ...reindexed] });
    }

    setHasUnsavedChanges(true);
    setShowExcelImport(false);
    setActiveTab('builder');
  };

  const saveLearningModule = async () => {
    if (savingRef.current) return;
    savingRef.current = true;

    if (!mod) {
      setSaveModalMessage("Construct the module first.");
      setSaveModalType('error');
      setShowSaveModal(true);
      savingRef.current = false;
      return;
    }

    // SAFETY GUARD: Prevent saving empty groups for existing modules (data loss prevention)
    if (id && (!mod.groups || mod.groups.length === 0)) {
      setSaveModalMessage("Cannot save: this module has no chapters/sections. This would wipe all existing content. If you intentionally want an empty module, please contact support.");
      setSaveModalType('error');
      setShowSaveModal(true);
      savingRef.current = false;
      return;
    }

    // Check for Article resources (typeId 6) missing content - warn user before save
    const articlesWithoutContent: string[] = [];
    mod.groups.forEach((group) => {
      group.steps.forEach((step) => {
        step.resources.forEach((r) => {
          if (r.resourceType === 'studyResource' && r.studyResourceTypeId === 6 && (!r.content || r.content.trim() === '')) {
            articlesWithoutContent.push(`"${r.name}" in ${step.name} (${group.name})`);
          }
        });
      });
    });

    if (articlesWithoutContent.length > 0) {
      const proceed = window.confirm(
        `The following Article resources are missing content (required):\n\n` +
        articlesWithoutContent.map(a => `• ${a}`).join('\n') +
        `\n\nPlease fill in the content for these resources before saving.\n\n` +
        `Tip: Use the "✨ Generate Content with AI" button in the Builder tab to auto-fill content.`
      );
      if (!proceed) {
        savingRef.current = false;
        return;
      }
    }

    console.log('[saveLearningModule] mod.name before save:', mod.name);
    console.log('[saveLearningModule] meta.title before save:', meta.title);

    setIsSaving(true);

    try {
      // Collect all files and build fileKey references
      const formData = new FormData();
      const payloadModule = JSON.parse(JSON.stringify(mod)); // Deep clone

      // Ensure the name is synced from meta.title (in case React state hasn't updated yet)
      payloadModule.name = meta.title;
      // Use the user-entered code if available, otherwise auto-generate from title
      if (!payloadModule.code) {
        payloadModule.code = slugify(meta.title).toUpperCase().replace(/-/g, "_");
      }

      console.log('[saveLearningModule] payloadModule.name after sync:', payloadModule.name);

      // If we have an id (from URL params), include it in the payload for updating
      if (id) {
        payloadModule.id = Number(id);
        console.log('[saveLearningModule] Setting payloadModule.id to:', Number(id));
      }

      mod.groups.forEach((group, gi) => {
        group.steps.forEach((step, si) => {
          step.resources.forEach((resource, ri) => {
            if (resource.file) {
              const fileKey = `resource_${gi}_${si}_${ri}`;
              formData.append(fileKey, resource.file);

              // Add fileKey to payload resource
              payloadModule.groups[gi].steps[si].resources[ri].fileKey = fileKey;
              // Remove file object from payload (not JSON serializable)
              delete payloadModule.groups[gi].steps[si].resources[ri].file;
            }
          });
        });
      });

      // Assign globally sequential sortOrder so ordering survives the DB round-trip
      // (chapters grouped correctly when reloaded with ORDER BY sortOrder)
      let globalSortOrder = 0;
      for (const group of payloadModule.groups) {
        if (!group.steps) continue;
        for (const step of group.steps) {
          globalSortOrder++;
          step.sortOrder = globalSortOrder;
          step.order = globalSortOrder;
        }
      }

      // Handle icon file upload
      if (mod.iconFile) {
        formData.append('moduleIcon', mod.iconFile);
        payloadModule.iconFileKey = 'moduleIcon';
      }
      delete payloadModule.iconFile; // Not JSON-serializable

      // Log the payload to see what tags are being sent
      console.log('[saveLearningModule] Full payload:', JSON.stringify(payloadModule, null, 2));

      formData.append('payload', JSON.stringify(payloadModule));

      const response = await ApiService.saveLearningModuleWithFiles(formData);

      // If response contains the saved module with id, update our state
      let moduleIdToRefetch = null;
      if (response.data && typeof response.data === 'object' && 'id' in response.data) {
        const savedModule = response.data as any as LearningModule;
        moduleIdToRefetch = savedModule.id;
        if (savedModule.id && !mod.id) {
          // Update the mod state with the returned id if it's a new module
          setMod({ ...mod, id: savedModule.id });
        }
      } else if (id) {
        // For updates, use the existing ID
        moduleIdToRefetch = Number(id);
      }

      // Store the module ID for refetching when modal closes
      setSavedModuleId(moduleIdToRefetch);

      // Reset unsaved changes indicator after successful save
      setHasUnsavedChanges(false);

      // const isNewModule = !id;
      // Calculate module statistics for summary
      const totalChapters = mod.groups.length;
      const totalSections = mod.groups.reduce((sum, g) => sum + g.steps.length, 0);
      const allResources = mod.groups.flatMap(g => g.steps.flatMap(step => step.resources));
      const totalResources = allResources.length;

      // Build resource breakdown by type
      const typeIconMap: Record<number, string> = { 13: '📕', 31: '🎬', 32: '▶️', 16: '🎧', 6: '📰', 5: '🔗' };
      const typeCounts = new Map<string, { icon: string; count: number }>();
      let testCount = 0;
      for (const r of allResources) {
        if (r.resourceType === 'test') {
          testCount++;
        } else if (r.studyResourceTypeId) {
          const rtName = resourceTypes.find(rt => rt.id === r.studyResourceTypeId)?.name || 'Other';
          const existing = typeCounts.get(rtName);
          if (existing) {
            existing.count++;
          } else {
            typeCounts.set(rtName, { icon: typeIconMap[r.studyResourceTypeId] || '📄', count: 1 });
          }
        }
      }
      const resourceBreakdown: Array<{ icon: string; label: string; value: number }> = [];
      typeCounts.forEach((val, label) => resourceBreakdown.push({ icon: val.icon, label, value: val.count }));
      if (testCount > 0) resourceBreakdown.push({ icon: '📝', label: 'Tests / Quizzes', value: testCount });

      setSaveModalData({ title: meta.title, chapters: totalChapters, sections: totalSections, resources: totalResources, resourceBreakdown });
      setSaveModalType('success');
      setShowSaveModal(true);
      console.log("Save response:", response.data);
    } catch (error: any) {
      console.error("Error saving module:", error);
      let errorMessage = "Failed to save module.";

      if (error.response) {
        const responseData = error.response.data;

        // Check if this is a validation error with detailed errors
        if (responseData?.validationErrors && Array.isArray(responseData.validationErrors)) {
          const validationErrors = responseData.validationErrors;

          errorMessage = `${responseData.message || 'Validation failed'}\n\n`;

          // Group errors by chapter › step so the user can locate the resource
          const errorsByLocation = new Map<string, Array<any>>();

          validationErrors.forEach((err: any) => {
            const location = err.groupName
              ? `${err.groupName} › ${err.stepName || 'Unknown Step'}`
              : (err.stepName || 'Unknown Step');
            if (!errorsByLocation.has(location)) {
              errorsByLocation.set(location, []);
            }
            errorsByLocation.get(location)!.push(err);
          });

          errorsByLocation.forEach((errors, location) => {
            errorMessage += `📍 Chapter/Step: ${location}\n`;
            errors.forEach((err: any) => {
              const idTag = err.resourceId ? ` (resource #${err.resourceId})` : '';
              errorMessage += `  ❌ "${err.resourceName}"${idTag}: ${err.error}\n`;
              if (err.isExisting) {
                errorMessage += `     ℹ️ This resource was saved earlier — it is not part of your current change. Fix it or delete it from the step above to unblock saving.\n`;
              }
            });
            errorMessage += '\n';
          });
        } else {
          const mainError = responseData?.error || "Save failed";
          const detail = responseData?.message || error.message;
          errorMessage = detail && detail !== mainError
            ? `${mainError}\n\n${detail}`
            : mainError;
        }
      } else if (error.request) {
        errorMessage = "Cannot connect to API server. Check if the server is running.";
      } else {
        errorMessage = error.message || "Unknown error occurred.";
      }

      setSaveModalMessage(errorMessage);
      setSaveModalType('error');
      setShowSaveModal(true);
    } finally {
      setIsSaving(false);
      savingRef.current = false;
    }
  };

  // AI Generation: Topics only (chapters + sections, no resources)
  const generateTopicsFromAI = async () => {
    if (!prompt.trim()) {
      setAiModalMessage("Please enter a prompt to generate the learning module topics.");
      setAiModalType('error');
      setShowAiModal(true);
      return;
    }

    setIsGenerating(true);

    try {
      const systemPrompt = `You are a learning module structure generator. Generate ONLY the chapter and section structure (topics) for a learning module. Do NOT include resources.

Return EXACT JSON matching this structure:

{
  "name": string,
  "code": string (UPPERCASE_SNAKE_CASE based on name),
  "status": 1,
  "sortOrder": 1,
  "remarks": string (brief description),
  "groups": [
    {
      "index": number (1-based),
      "name": string (e.g. "Chapter 1: Introduction"),
      "color": string (hex color code like "#2572EB"),
      "steps": [
        {
          "name": string (DESCRIPTIVE name e.g. "Introduction to Variables", "Basic Syntax"),
          "code": string (UPPERCASE_SNAKE_CASE),
          "order": number (1-based within chapter),
          "sortOrder": number (same as order),
          "startDate": null,
          "endDate": null,
          "group": string (must match parent group name),
          "resources": []
        }
      ]
    }
  ]
}

CRITICAL RULES:
1. Return ONLY valid JSON - no markdown code blocks, no explanations, no extra text
2. Use consistent color palette for groups (e.g. "#2572EB", "#C1004F", "#D39D09", "#107C10", "#5C2E91")
3. Generate the number of groups (chapters) and steps (sections) that the user requests. If not specified, default to 3-5 groups with 3-5 steps each
4. Use proper UPPERCASE_SNAKE_CASE for all code fields
5. Set resources to an empty array [] for every step - resources will be added separately later
6. The response must be parseable by JSON.parse()
7. CRITICAL: Use DESCRIPTIVE, MEANINGFUL names for all sections - NOT generic numbered names
8. IMPORTANT: Read the user's request carefully. If they specify a number of chapters or topics, generate EXACTLY that many

User request: ${prompt}

Additional context: Name="${meta.title}", Audience="${meta.audience}", Difficulty="${meta.difficulty}", Duration=${meta.durationMins}mins, Tone="${meta.tone}"`;

      const response = await ApiService.generateNewCourseStreaming(
        { prompt: systemPrompt, topicIds: mod?.selectedTopics || [] },
        (chunk: string) => {
          console.log('Received chunk:', chunk);
        }
      );

      const responseData = response.data as any;
      const apiResponse = responseData.response as string;

      // Check for truncation
      if (responseData.stop_reason === 'max_tokens') {
        throw new Error("AI response was truncated due to length limits. Try requesting fewer chapters.");
      }

      // Parse the response
      let cleanedResponse = apiResponse.trim();

      // Try to extract JSON from markdown code blocks first
      const jsonMatch = cleanedResponse.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        cleanedResponse = jsonMatch[1].trim();
      }

      // Find the first { and last } to extract just the JSON object
      const firstBrace = cleanedResponse.indexOf('{');
      const lastBrace = cleanedResponse.lastIndexOf('}');

      if (firstBrace !== -1 && lastBrace !== -1) {
        cleanedResponse = cleanedResponse.substring(firstBrace, lastBrace + 1);
      }

      let generatedModule: LearningModule;

      try {
        generatedModule = JSON.parse(cleanedResponse) as LearningModule;
      } catch (e: any) {
        console.error("Raw API response that failed to parse:", apiResponse);
        console.error("Parse error:", e);

        if (e.message && (e.message.includes("Unexpected end") || e.message.includes("Expected"))) {
          throw new Error("AI response was incomplete or truncated. Try a shorter prompt or fewer chapters.");
        }
        throw new Error("Could not parse AI response as JSON: " + e.message);
      }

      if (!generatedModule.name || !generatedModule.groups || !Array.isArray(generatedModule.groups)) {
        throw new Error("Invalid module structure returned from AI");
      }

      // Ensure every step has an empty resources array
      generatedModule.groups.forEach((group, chapterIndex) => {
        if (group.steps && Array.isArray(group.steps)) {
          group.steps.forEach((step) => {
            step.resources = step.resources || [];
          });
        }
      });

      // Merge only generated groups into existing module — preserve user-set fields
      // (lessonPlanType, lessonPlanSubTypeId, selectedTopics, selectedBatches, analyticsDashboard, etc.)
      setMod(prev => prev ? { ...prev, groups: generatedModule.groups } : prev);

      // Build success message with document usage summary
      let successMsg = "Module topics generated successfully! Switch to the Builder tab to add resources for each chapter.";
      const docsUsed = responseData.documentsUsed as { name: string; chars: number }[] | undefined;
      if (docsUsed && docsUsed.length > 0) {
        const docSummary = docsUsed.map((d: { name: string; chars: number }) => `${d.name} (${Math.round(d.chars / 1000)}K chars)`).join(", ");
        successMsg += `\n\nAI consulted ${docsUsed.length} document${docsUsed.length > 1 ? "s" : ""}: ${docSummary}`;
      }

      setAiModalMessage(successMsg);
      setAiModalType('success');
      setShowAiModal(true);

    } catch (error: any) {
      console.error("Error generating topics:", error);
      let errorMessage = "Please try again.";

      if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.details || error.message;
      } else if (error.request) {
        errorMessage = "Cannot connect to API server. Check if the server is running and CORS is enabled.";
      } else {
        errorMessage = error.message || "Unknown error occurred.";
      }

      setAiModalMessage("Failed to generate topics: " + errorMessage);
      setAiModalType('error');
      setShowAiModal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  // Load AI config status for all study resources in the module
  const loadAiConfigStatus = async (module: LearningModule) => {
    const resourceIds: number[] = [];
    for (const group of module.groups || []) {
      for (const step of group.steps || []) {
        for (const resource of step.resources || []) {
          if (resource.id && resource.resourceType === 'studyResource') {
            resourceIds.push(resource.id);
          }
        }
      }
    }
    if (resourceIds.length === 0) return;

    const uniqueIds = Array.from(new Set(resourceIds));
    try {
      const result = await AiWorkspaceService.batchCheckConfigStatus(uniqueIds);
      if (result.success && result.data) {
        setAiConfiguredResourceIds(new Set(result.data));
      }
    } catch (err) {
      console.error('[LM Builder] Error loading AI config status:', err);
    }
  };

  // AI Generation: Resources for a specific section (step)
  const generateResourcesForSection = async (chapterIndex: number, sectionIndex: number, conversationContext?: Array<{ role: string; content: string }>, courseDocumentMode?: "off" | "priority" | "strict") => {
    if (!mod) return;

    const chapter = mod.groups[chapterIndex];
    if (!chapter) return;

    const section = chapter.steps[sectionIndex];
    if (!section) return;

    const sectionKey = `${chapterIndex}-${sectionIndex}`;
    setAiLoadingStates(prev => ({ ...prev, [sectionKey]: true }));

    try {
      // Only include resource types that are valid for saving in lesson plans
      const validTypeIds = [1, 5, 6, 13, 31, 32, 37];
      const resourceTypesForPrompt = resourceTypes
        .filter(type => validTypeIds.includes(type.id))
        .map(type => ({
          id: type.id,
          name: type.name,
          subtypes: type.subtypes?.map(st => ({ id: st.id, name: st.name })) || []
        }));

      const systemPrompt = `You are a learning resource generator. Generate study resources for a single section of a learning module.

SECTION: "${section.name}"
CHAPTER: "${chapter.name}"
MODULE: "${meta.title}", Audience="${meta.audience}", Difficulty="${meta.difficulty}", Tone="${meta.tone}"

Return EXACT JSON matching this structure:

{
  "resources": [
    {
      "name": string (DESCRIPTIVE name, e.g. "Introduction Video", "Syntax Guide PDF"),
      "resourceType": "studyResource",
      "studyResourceTypeId": number (pick from available types below),
      "studyResourceSubTypeId": number or null,
      "content": string (content for Article type - keep BRIEF, 1-2 paragraphs),
      "url": string or null (URL for video/reference types),
      "sortOrder": number (1-based)
    }
  ]
}

AVAILABLE RESOURCE TYPES (use ONLY these IDs):
${JSON.stringify(resourceTypesForPrompt, null, 2)}

RESOURCE TYPE RULES:
- Type 6 (Article): MUST include "content" with BRIEF text (1-2 paragraphs). "url" can be null.
- Type 5 (Reference): MUST include "url" with a valid web URL.
- Type 32 (YouTube Video): MUST include "url" with a YouTube video URL.
- Type 13 (E-Book): MUST include "url" with a link to a freely accessible e-book / PDF (e.g. from archive.org, gutenberg.org, an .edu page, or a public PDF).
- ONLY use type IDs from the list above.
- Prefer type 6 (Article) for text-based content since it requires no external URL or file.

CRITICAL RULES:
1. Return ONLY valid JSON - no markdown, no explanations
2. Generate 2-3 resources for this section
3. Use DESCRIPTIVE names
4. The response must be parseable by JSON.parse()${conversationContext && conversationContext.length > 0
  ? `\n\nTEACHER'S INSTRUCTIONS (follow these preferences closely):\n${conversationContext.filter(m => m.role === "user").map(m => m.content).join("\n")}`
  : ""}`;

      const useCourseDoc = courseDocumentMode && courseDocumentMode !== "off";
      const response = await ApiService.generateLMResources({
        systemPrompt,
        topicIds: useCourseDoc ? (mod?.selectedTopics || []) : undefined,
        courseDocumentMode: useCourseDoc ? courseDocumentMode : undefined,
      });

      const responseData = response.data as any;
      const resourceList = responseData.resources as any[];

      if (!resourceList || !Array.isArray(resourceList)) {
        throw new Error("Invalid resource structure returned from AI");
      }

      // Build resource objects from the backend response (already parsed + URLs resolved)
      const allNewResources: Resource[] = resourceList.map((r: any, idx: number) => ({
        name: r.name || `Resource ${idx + 1}`,
        resourceType: (r.resourceType || "studyResource") as "studyResource" | "test",
        studyResourceTypeId: r.studyResourceTypeId,
        studyResourceSubTypeId: r.studyResourceSubTypeId || null,
        content: r.content || undefined,
        url: r.url || undefined,
        sortOrder: idx + 1,
      }));

      // Filter out resources whose URL already exists in the module
      const existingUrls = collectExistingUrls(mod);
      const seenInBatch = new Set<string>();
      const newResources: Resource[] = [];
      const duplicates: string[] = [];

      for (const r of allNewResources) {
        if (r.url) {
          const norm = normalizeUrlForDedup(r.url);
          if (existingUrls.has(norm) || seenInBatch.has(norm)) {
            duplicates.push(r.name);
            continue;
          }
          seenInBatch.add(norm);
        }
        newResources.push(r);
      }

      if (duplicates.length > 0) {
        console.info(`[LM Builder] Filtered ${duplicates.length} duplicate URL(s):`, duplicates);
      }

      // Push to modal for review instead of immediately bolting on
      setAiGenerateModal(prev => ({
        ...prev,
        generatedResources: newResources,
        isGenerating: false,
        generationError: duplicates.length > 0 && newResources.length === 0
          ? `All generated resources had duplicate URLs already in this module. Try generating again.`
          : null,
      }));

    } catch (error: any) {
      console.error("Error generating resources for section:", error);
      let errorMessage = error.message || "Unknown error occurred.";

      if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.details || error.message;
      } else if (error.request) {
        errorMessage = "Cannot connect to API server. Check if the server is running.";
      }

      // Show error in modal instead of alert
      setAiGenerateModal(prev => ({
        ...prev,
        isGenerating: false,
        generationError: errorMessage,
      }));
    } finally {
      setAiLoadingStates(prev => {
        const next = { ...prev };
        delete next[sectionKey];
        return next;
      });
    }
  };

  // AI Generation: Quiz from PDFs attached to a step
  const generateAiQuiz = async (chapterIndex: number, sectionIndex: number, conversationContext?: Array<{ role: string; content: string }>, bloomsEnabled?: boolean, courseDocumentMode?: "off" | "priority" | "strict") => {
    if (!mod) return;

    const chapter = mod.groups[chapterIndex];
    if (!chapter) return;
    const step = chapter.steps[sectionIndex];
    if (!step) return;

    // Collect PDF URLs from the step's resources
    const pdfUrls = step.resources
      .filter(
        (r) =>
          r.resourceType === "studyResource" &&
          (r.studyResourceTypeId === 13 || r.studyResourceTypeId === 1) &&
          (r.url || r.fileUrl),
      )
      .map((r) => (r.url || r.fileUrl) as string);

    // Collect article IDs (TypeId 6) that have a DB id
    const articleIds = step.resources
      .filter(
        (r) =>
          r.resourceType === "studyResource" &&
          r.studyResourceTypeId === 6 &&
          r.id,
      )
      .map((r) => r.id as number);

    const useCourseDoc = courseDocumentMode && courseDocumentMode !== "off";
    const hasCourseDocContext = useCourseDoc && mod?.selectedTopics && mod.selectedTopics.length > 0;

    if (pdfUrls.length === 0 && articleIds.length === 0 && !hasCourseDocContext) return;

    try {
      const response = await ApiService.aiPreviewStepQuiz({
        pdfUrls,
        articleIds: articleIds.length > 0 ? articleIds : undefined,
        stepName: step.name,
        chapterName: chapter.name,
        moduleName: mod.name || "",
        questionCount: lmConfig?.aiQuizQuestionCount || 4,
        testDate: step.startDate || undefined,
        bloomsTagTypeId: (bloomsEnabled !== false) ? iteachConfig.bloomsTagTypeId : undefined,
        conversationContext,
        topicIds: hasCourseDocContext ? mod.selectedTopics : undefined,
        courseDocumentMode: useCourseDoc ? courseDocumentMode : undefined,
      });

      const { questions } = response.data.data;

      // Show questions in modal review view
      setAiGenerateModal(prev => ({
        ...prev,
        generatedQuestions: questions,
        isGenerating: false,
        generationError: null,
      }));
    } catch (error: any) {
      console.error("Error generating AI quiz preview:", error);
      let errorMessage = "Failed to generate quiz.";
      if (error.response?.status === 429) {
        errorMessage =
          error.response.data?.error || "AI usage limit exceeded. Try again later.";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setAiGenerateModal(prev => ({
        ...prev,
        isGenerating: false,
        generationError: errorMessage,
      }));
    }
  };

  // Batch generate AI quizzes for all steps in a given phase column
  const batchGenerateAiQuiz = async (phase: string, phaseTags: Tag[], conversationContext?: Array<{ role: string; content: string }>, bloomsEnabled?: boolean) => {
    if (!mod) return;
    if (batchQuizRunningRef.current) return;
    batchQuizRunningRef.current = true;

    const loadingKey = `batch-${phase}`;
    setAiLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

    try {
    // Flatten all steps
    const allStepInfos: {
      ci: number; si: number;
      chapterName: string; stepName: string; step: Step;
    }[] = [];
    mod.groups.forEach((chapter, ci) => {
      chapter.steps.forEach((step, si) => {
        allStepInfos.push({ ci, si, chapterName: chapter.name, stepName: step.name, step });
      });
    });

    const phaseTagIds = phaseTags.map((t) => t.id);
    let created = 0;
    let skipped = 0;
    let failed = 0;

    setBatchQuizProgress({
      phase, total: allStepInfos.length, current: 0,
      created: 0, skipped: 0, failed: 0, currentStepName: "", done: false,
    });

    const updatedMod = JSON.parse(JSON.stringify(mod)) as LearningModule;

    for (let i = 0; i < allStepInfos.length; i++) {
      const { ci, si, chapterName, stepName, step } = allStepInfos[i];

      setBatchQuizProgress((prev) =>
        prev ? { ...prev, current: i + 1, currentStepName: stepName } : prev
      );

      // Skip if step already has a test tagged for this phase
      const hasPhaseTest = step.resources.some(
        (r) =>
          r.resourceType === "test" &&
          r.tags &&
          r.tags.some((tag) => phaseTagIds.includes(tag.id))
      );
      if (hasPhaseTest) {
        skipped++;
        setBatchQuizProgress((prev) => prev ? { ...prev, skipped } : prev);
        continue;
      }

      // Collect PDF URLs
      const pdfUrls = step.resources
        .filter(
          (r) =>
            r.resourceType === "studyResource" &&
            (r.studyResourceTypeId === 13 || r.studyResourceTypeId === 1) &&
            (r.url || r.fileUrl)
        )
        .map((r) => (r.url || r.fileUrl) as string);

      // Collect article IDs (TypeId 6)
      const articleIds = step.resources
        .filter(
          (r) =>
            r.resourceType === "studyResource" &&
            r.studyResourceTypeId === 6 &&
            r.id
        )
        .map((r) => r.id as number);

      if (pdfUrls.length === 0 && articleIds.length === 0) {
        skipped++;
        setBatchQuizProgress((prev) => prev ? { ...prev, skipped } : prev);
        continue;
      }

      try {
        const response = await ApiService.aiGenerateStepQuiz({
          pdfUrls,
          articleIds: articleIds.length > 0 ? articleIds : undefined,
          stepName,
          chapterName,
          moduleName: mod.name || "",
          questionCount: lmConfig?.aiQuizQuestionCount || 4,
          testDate: step.startDate || undefined,
          bloomsTagTypeId: (bloomsEnabled !== false) ? iteachConfig.bloomsTagTypeId : undefined,
          phaseLabel: phase,
          conversationContext,
        });

        const { testId, testName } = response.data.data;
        const targetStep = updatedMod.groups[ci].steps[si];
        targetStep.resources.push({
          name: testName,
          resourceType: "test",
          testId: String(testId),
          testName,
          sortOrder: targetStep.resources.length + 1,
          tags: [...phaseTags],
        });

        created++;
        setBatchQuizProgress((prev) => prev ? { ...prev, created } : prev);
      } catch (error: any) {
        console.error(`[batchGenerateAiQuiz] Failed for step "${stepName}":`, error);
        failed++;
        setBatchQuizProgress((prev) => prev ? { ...prev, failed } : prev);
      }
    }

    setMod(updatedMod);
    if (created > 0) setHasUnsavedChanges(true);

    setBatchQuizProgress((prev) => prev ? { ...prev, done: true } : prev);
    setAiLoadingStates((prev) => {
      const next = { ...prev };
      delete next[loadingKey];
      return next;
    });

    const parts: string[] = [];
    if (created > 0) parts.push(`${created} quiz${created > 1 ? "zes" : ""} created`);
    if (skipped > 0) parts.push(`${skipped} step${skipped > 1 ? "s" : ""} skipped`);
    if (failed > 0) parts.push(`${failed} failed`);

    setFeedbackType(failed > 0 && created === 0 ? "error" : "success");
    setFeedbackMessage(
      created > 0
        ? <>Batch quiz generation complete: {parts.join(", ")}. <span style={{ color: "var(--status-danger)", fontWeight: 600 }}>Save the module to persist changes.</span></>
        : `Batch quiz generation complete: ${parts.join(", ")}.`
    );
    setShowFeedbackModal(true);
    } finally {
      batchQuizRunningRef.current = false;
    }
  };

  // Handler for closing save modal
  const handleCloseSaveModal = async () => {
    setShowSaveModal(false);

    // If it was a successful save, refetch the module data to get fresh IDs
    if (saveModalType === 'success' && savedModuleId) {
      console.log('[handleCloseSaveModal] Refetching module data for ID:', savedModuleId);
      await refetchModuleData(savedModuleId);
      setSavedModuleId(null); // Reset after refetching

      // Switch to view mode after save
      console.log('[handleCloseSaveModal] Redirecting to view mode');
      history.push(`/lm-builder/${savedModuleId}?mode=view`);
    }
  };

  // Mutators
  const updateMeta = (patch: Partial<ModuleMeta>) => {
    const next = { ...meta, ...patch } as ModuleMeta;
    console.log('[updateMeta] Updating meta:', patch);
    console.log('[updateMeta] mod exists:', !!mod);
    setMeta(next);
    if (mod) {
      console.log('[updateMeta] Updating mod.name to:', next.title);
      setMod({
        ...mod,
        name: next.title,
        code: slugify(next.title).toUpperCase().replace(/-/g, "_")
      });
    } else {
      console.log('[updateMeta] mod is null, cannot update mod.name');
    }
  };

  const COLORS = ["#2572EB", "#C1004F", "#D39D09", "#107C10", "#5C2E91"];

  const addChapter = () => {
    if (!mod) return alert("Create a module first.");
    const newIndex = mod.groups.length + 1;
    setMod({
      ...mod,
      groups: [...mod.groups, {
        index: newIndex,
        name: `Chapter ${newIndex}`,
        color: COLORS[(newIndex - 1) % COLORS.length],
        steps: []
      }]
    });
  };
  const removeChapter = (ci: number) => mod && setMod({
    ...mod,
    groups: mod.groups.filter((_, i) => i !== ci).map((g, idx) => ({ ...g, index: idx + 1 }))
  });
  const reorderChapter = (ci: number, dir: -1 | 1) => {
    if (!mod) return;
    const to = ci + dir;
    if (to < 0 || to >= mod.groups.length) return;
    setMod({ ...mod, groups: move(mod.groups, ci, to).map((g, idx) => ({ ...g, index: idx + 1 })) });
  };

  const updateChapter = (ci: number, patch: Partial<Group>) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    groups[ci] = { ...groups[ci], ...patch } as Group;
    // Update group name in all child steps
    if (patch.name) {
      groups[ci].steps = groups[ci].steps.map(s => ({ ...s, group: patch.name! }));
    }
    setMod({ ...mod, groups });
  };
  const addSection = (ci: number) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    const chapterIndex = groups[ci].index; // Chapter number (1-based)
    const sectionIndex = groups[ci].steps.length + 1; // Section number within chapter (1-based)
    const newOrder = sectionIndex;
    const sectionName = `Section ${chapterIndex}.${sectionIndex}`;
    groups[ci] = {
      ...groups[ci],
      steps: [...groups[ci].steps, defaultStep(sectionName, groups[ci].name, newOrder)]
    };
    setMod({ ...mod, groups });
  };
  const removeSection = (ci: number, si: number) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    groups[ci] = {
      ...groups[ci],
      steps: groups[ci].steps.filter((_, i) => i !== si).map((s, idx) => ({
        ...s,
        order: idx + 1,
        sortOrder: idx + 1
      }))
    };
    setMod({ ...mod, groups });
  };
  const reorderSection = (ci: number, si: number, dir: -1 | 1) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    const to = si + dir;
    if (to < 0 || to >= groups[ci].steps.length) return;
    groups[ci].steps = move(groups[ci].steps, si, to).map((s, idx) => ({
      ...s,
      order: idx + 1,
      sortOrder: idx + 1
    }));
    setMod({ ...mod, groups });
  };
  const updateSection = (ci: number, si: number, patch: Partial<Step>) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    const steps = groups[ci].steps.slice();
    steps[si] = { ...steps[si], ...patch } as Step;
    // Update code if name changed
    if (patch.name) {
      steps[si].code = slugify(patch.name).toUpperCase().replace(/-/g, "_");
    }
    groups[ci].steps = steps;
    setMod({ ...mod, groups });
  };

  const addResource = (ci: number, si: number) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    // Default new resources to Download (Id 1) — resourceTypes is sorted
    // alphabetically for display, so [0] is no longer a meaningful default.
    const firstType = resourceTypes.find((t) => t.id === 1) || (resourceTypes.length > 0 ? resourceTypes[0] : null);
    const firstTypeId = firstType?.id || 1;
    const firstSubTypeId = firstType?.subtypes && firstType.subtypes.length > 0 ? firstType.subtypes[0].id : undefined;
    const resourceIndex = groups[ci].steps[si].resources.length + 1; // 1-based index within section
    const newResource: Resource = {
      name: `Resource ${resourceIndex}`,
      resourceType: "studyResource",
      studyResourceTypeId: firstTypeId,
      studyResourceSubTypeId: firstSubTypeId,
      content: "",
      sortOrder: resourceIndex
    };
    const steps = groups[ci].steps.slice();
    const s = steps[si];
    steps[si] = { ...s, resources: [...s.resources, newResource] };
    groups[ci].steps = steps;
    setMod({ ...mod, groups });
  };
  const removeResource = (ci: number, si: number, ri: number) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    const steps = groups[ci].steps.slice();
    const resources = steps[si].resources.filter((_, i) => i !== ri).map((r, idx) => ({
      ...r,
      sortOrder: idx + 1
    }));
    steps[si] = { ...steps[si], resources };
    groups[ci].steps = steps;
    setMod({ ...mod, groups });
  };
  const updateResource = (ci: number, si: number, ri: number, patch: Partial<Resource>) => {
    if (!mod) return;
    console.log('[updateResource] Updating resource:', { ci, si, ri, patch });
    const groups = mod.groups.slice();
    const steps = groups[ci].steps.slice();
    const resources = steps[si].resources.slice();
    resources[ri] = { ...resources[ri], ...patch } as Resource;
    steps[si] = { ...steps[si], resources };
    groups[ci].steps = steps;
    setMod({ ...mod, groups });
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };
  const reorderResource = (ci: number, si: number, ri: number, dir: -1 | 1) => {
    if (!mod) return;
    const groups = mod.groups.slice();
    const steps = groups[ci].steps.slice();
    const resources = steps[si].resources.slice();
    const to = ri + dir;
    if (to < 0 || to >= resources.length) return;
    steps[si] = {
      ...steps[si],
      resources: move(resources, ri, to).map((r, idx) => ({ ...r, sortOrder: idx + 1 }))
    };
    groups[ci].steps = steps;
    setMod({ ...mod, groups });
  };

  // Toggle functions for collapsible sections
  const toggleChapter = (ci: number) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(ci)) {
      newExpanded.delete(ci);
    } else {
      newExpanded.add(ci);
    }
    setExpandedChapters(newExpanded);
  };

  const toggleSection = (ci: number, si: number) => {
    const key = `${ci}-${si}`;
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSections(newExpanded);
  };

  // Expand/collapse all functions
  const expandAll = () => {
    if (!mod) return;
    const allChapters = new Set(mod.groups.map((_, idx) => idx));
    const allSections = new Set<string>();
    mod.groups.forEach((group, ci) => {
      group.steps.forEach((_, si) => {
        allSections.add(`${ci}-${si}`);
      });
    });
    setExpandedChapters(allChapters);
    setExpandedSections(allSections);
  };

  const collapseAll = () => {
    setExpandedChapters(new Set());
    setExpandedSections(new Set());
  };

  // ============ AI Helper Functions ============

  // AI: Analyze module and suggest metadata
  const analyzeModuleWithAI = async () => {
    if (!mod) return;

    setIsAnalyzingModule(true);
    try {
      const response = await ApiService.aiAnalyzeModule({
        moduleName: mod.name,
        audience: meta.audience,
        chapters: mod.groups.map(g => ({
          name: g.name,
          sections: g.steps.map(s => ({
            name: s.name,
            resources: s.resources.map(r => ({
              name: r.name,
              type: resourceTypes.find(rt => rt.id === r.studyResourceTypeId)?.name || 'Unknown'
            }))
          }))
        }))
      });

      const responseData = response.data as any;
      const { difficulty, estimatedDuration, tone, reasoning } = responseData;

      updateMeta({
        difficulty: difficulty as ModuleMeta["difficulty"],
        durationMins: estimatedDuration,
        tone: tone as ModuleMeta["tone"]
      });

      console.log("AI Analysis reasoning:", reasoning);
      alert(`AI suggested: ${difficulty} level, ${estimatedDuration} mins, ${tone} tone.\n\nReasoning: ${reasoning}`);
    } catch (error: any) {
      console.error("Error analyzing module:", error);
      alert("Failed to analyze module: " + (error.response?.data?.error || error.message));
    } finally {
      setIsAnalyzingModule(false);
    }
  };

  // AI: Suggest tags for a resource
  const suggestTagsForResource = async (ci: number, si: number, ri: number) => {
    if (!mod) return;

    const resource = mod.groups[ci].steps[si].resources[ri];
    const resourceTypeName = resourceTypes.find(rt => rt.id === resource.studyResourceTypeId)?.name;

    const key = `tags-${ci}-${si}-${ri}`;
    setAiLoadingStates(prev => ({ ...prev, [key]: true }));

    try {
      const response = await ApiService.aiSuggestTags({
        resourceName: resource.name,
        resourceContent: resource.content,
        resourceType: resourceTypeName,
        availableTags: availableTags.map(t => ({
          id: t.id,
          name: t.name,
          code: t.code,
          typeName: t.typeName
        }))
      });

      const responseData = response.data as any;
      const { suggestedTagIds, reasoning } = responseData;

      // Find the actual tag objects
      const suggestedTags = availableTags.filter(t => suggestedTagIds.includes(t.id));

      // Update resource with suggested tags
      updateResource(ci, si, ri, { tags: suggestedTags });

      console.log("AI tag suggestion reasoning:", reasoning);
      alert(`AI suggested ${suggestedTags.length} tags.\n\nReasoning: ${reasoning}`);
    } catch (error: any) {
      console.error("Error suggesting tags:", error);
      alert("Failed to suggest tags: " + (error.response?.data?.error || error.message));
    } finally {
      setAiLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  // AI: Extract URL metadata
  const extractUrlMetadata = async (ci: number, si: number, ri: number, url: string) => {
    if (!url || !mod) return;

    const resource = mod.groups[ci].steps[si].resources[ri];
    const key = `url-${ci}-${si}-${ri}`;
    setAiLoadingStates(prev => ({ ...prev, [key]: true }));

    try {
      const response = await ApiService.aiExtractUrlMetadata(url);
      const responseData = response.data as any;
      const { title, description } = responseData;

      // Update resource name with extracted title
      if (title && title !== url) {
        updateResource(ci, si, ri, {
          name: title,
          content: description || resource.content
        });
        console.log(`Extracted metadata: ${title}`);
      }
    } catch (error: any) {
      console.error("Error extracting URL metadata:", error);
      // Silent fail - not critical
    } finally {
      setAiLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  // AI: Generate content for a resource
  const generateResourceContent = async (ci: number, si: number, ri: number, conversationContext?: Array<{ role: string; content: string }>) => {
    if (!mod) return;

    const resource = mod.groups[ci].steps[si].resources[ri];
    const section = mod.groups[ci].steps[si];
    const chapter = mod.groups[ci];
    const resourceTypeName = resourceTypes.find(rt => rt.id === resource.studyResourceTypeId)?.name || 'Unknown';

    const key = `content-${ci}-${si}-${ri}`;
    setAiLoadingStates(prev => ({ ...prev, [key]: true }));

    try {
      const response = await ApiService.aiGenerateResourceContent({
        resourceName: resource.name,
        resourceType: resourceTypeName,
        sectionName: section.name,
        chapterName: chapter.name,
        moduleContext: {
          title: mod.name,
          audience: meta.audience,
          difficulty: meta.difficulty,
          tone: meta.tone
        },
        lessonPlanId: id ? Number(id) : undefined,
        conversationContext,
      });

      const responseData = response.data as any;
      const { content, suggestions } = responseData;

      // Update resource with generated content
      const updates: Partial<Resource> = { content };
      if (suggestions?.url) {
        updates.url = suggestions.url;
      }

      updateResource(ci, si, ri, updates);

      alert("Content generated successfully!");
    } catch (error: any) {
      console.error("Error generating content:", error);
      alert("Failed to generate content: " + (error.response?.data?.error || error.message));
    } finally {
      setAiLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  // ===== AI Generate Modal: open/close/handle =====

  const openAiGenerateModal = (
    type: GenerationType,
    opts: {
      chapterIndex?: number;
      sectionIndex?: number;
      resourceIndex?: number;
      phaseName?: string;
      phaseTags?: Tag[];
    } = {}
  ) => {
    if (!mod) return;

    const ctx: AiGenerateModalContext = {
      moduleName: mod.name,
      audience: meta.audience,
      difficulty: meta.difficulty,
    };

    if (opts.chapterIndex !== undefined) {
      const chapter = mod.groups[opts.chapterIndex];
      ctx.chapterName = chapter?.name;
      if (opts.sectionIndex !== undefined) {
        const section = chapter?.steps[opts.sectionIndex];
        ctx.sectionName = section?.name;
        if (opts.resourceIndex !== undefined) {
          const resource = section?.resources[opts.resourceIndex];
          ctx.resourceName = resource?.name;
          ctx.resourceType = resourceTypes.find(rt => rt.id === resource?.studyResourceTypeId)?.name;
        }
      }
    }

    if (opts.phaseName) ctx.phaseName = opts.phaseName;

    if (type === "quiz" || type === "quiz-all") {
      ctx.questionCount = lmConfig?.aiQuizQuestionCount || 4;
      ctx.bloomsEnabled = !!iteachConfig.bloomsTagTypeId;
    }

    setAiGenerateModal({
      isOpen: true,
      type,
      context: ctx,
      ...opts,
    });
  };

  const closeAiGenerateModal = () => {
    setAiGenerateModal(prev => ({
      ...prev,
      isOpen: false,
      generatedResources: undefined,
      generatedQuestions: undefined,
      isGenerating: false,
      generationError: null,
    }));
  };

  const handleAiGenerateNow = (options?: GenerationOptions) => {
    const { type, chapterIndex, sectionIndex, resourceIndex, phaseName, phaseTags } = aiGenerateModal;
    const docMode = options?.courseDocumentMode || "off";
    if (type === "content" && chapterIndex !== undefined && sectionIndex !== undefined && resourceIndex !== undefined) {
      closeAiGenerateModal();
      generateResourceContent(chapterIndex, sectionIndex, resourceIndex);
    } else if (type === "resources" && chapterIndex !== undefined && sectionIndex !== undefined) {
      // Keep modal open for resources — show loading, then review view
      setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null, generatedResources: undefined }));
      generateResourcesForSection(chapterIndex, sectionIndex, undefined, docMode);
      return; // Don't close modal
    } else if (type === "quiz" && chapterIndex !== undefined && sectionIndex !== undefined) {
      // Keep modal open for quiz — show loading, then quiz review view
      const bloomsEnabled = options?.bloomsEnabled ?? true;
      setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null, generatedQuestions: undefined, quizBloomsEnabled: bloomsEnabled }));
      generateAiQuiz(chapterIndex, sectionIndex, undefined, bloomsEnabled, docMode);
      return; // Don't close modal
    } else if (type === "quiz-all" && phaseName && phaseTags) {
      closeAiGenerateModal();
      const bloomsEnabled = options?.bloomsEnabled ?? true;
      batchGenerateAiQuiz(phaseName, phaseTags, undefined, bloomsEnabled);
    }
  };

  const handleAiGenerateWithInstructions = (chatMessages: AiChatMessage[], options?: GenerationOptions) => {
    const conversationContext = chatMessages.map(m => ({ role: m.role, content: m.content }));
    const { type, chapterIndex, sectionIndex, resourceIndex, phaseName, phaseTags } = aiGenerateModal;
    const docMode = options?.courseDocumentMode || "off";
    if (type === "content" && chapterIndex !== undefined && sectionIndex !== undefined && resourceIndex !== undefined) {
      closeAiGenerateModal();
      generateResourceContent(chapterIndex, sectionIndex, resourceIndex, conversationContext);
    } else if (type === "resources" && chapterIndex !== undefined && sectionIndex !== undefined) {
      // Keep modal open for resources — show loading, then review view
      setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null, generatedResources: undefined }));
      generateResourcesForSection(chapterIndex, sectionIndex, conversationContext, docMode);
      return; // Don't close modal
    } else if (type === "quiz" && chapterIndex !== undefined && sectionIndex !== undefined) {
      // Keep modal open for quiz — show loading, then quiz review view
      const bloomsEnabled = options?.bloomsEnabled ?? true;
      setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null, generatedQuestions: undefined, quizBloomsEnabled: bloomsEnabled }));
      generateAiQuiz(chapterIndex, sectionIndex, conversationContext, bloomsEnabled, docMode);
      return; // Don't close modal
    } else if (type === "quiz-all" && phaseName && phaseTags) {
      closeAiGenerateModal();
      const bloomsEnabled = options?.bloomsEnabled ?? true;
      batchGenerateAiQuiz(phaseName, phaseTags, conversationContext, bloomsEnabled);
    }
  };

  // Confirm reviewed resources and bolt them onto the lesson plan
  const handleConfirmResources = (resources: Resource[]) => {
    const { chapterIndex, sectionIndex } = aiGenerateModal;
    if (!mod || chapterIndex === undefined || sectionIndex === undefined) return;

    const updatedMod = JSON.parse(JSON.stringify(mod)) as LearningModule;
    const existing = updatedMod.groups[chapterIndex].steps[sectionIndex].resources || [];

    const finalResources = resources.map((r, idx) => ({
      ...r,
      sortOrder: existing.length + idx + 1,
    }));

    updatedMod.groups[chapterIndex].steps[sectionIndex].resources = [...existing, ...finalResources];
    setMod(updatedMod);
    closeAiGenerateModal();
  };

  // Regenerate replacements for rejected resources
  const regenerateRejectedResources = async (rejected: Array<{ name: string; studyResourceTypeId?: number }>, feedback: string) => {
    const { chapterIndex, sectionIndex } = aiGenerateModal;
    if (!mod || chapterIndex === undefined || sectionIndex === undefined) return;

    setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null }));

    const chapter = mod.groups[chapterIndex];
    const section = chapter?.steps[sectionIndex];
    if (!chapter || !section) return;

    const validTypeIds = [1, 5, 6, 13, 31, 32, 37];
    const resourceTypesForPrompt = resourceTypes
      .filter(type => validTypeIds.includes(type.id))
      .map(type => ({
        id: type.id,
        name: type.name,
        subtypes: type.subtypes?.map(st => ({ id: st.id, name: st.name })) || []
      }));

    // Build rejected list with type names so Claude replaces with same types
    const rejectedDescriptions = rejected.map(r => {
      const typeName = resourceTypes.find(rt => rt.id === r.studyResourceTypeId)?.name || "Unknown";
      return `- "${r.name}" (type: ${typeName}, studyResourceTypeId: ${r.studyResourceTypeId})`;
    }).join("\n");

    const rejectedNames = rejected.map(r => r.name);

    const systemPrompt = `You are a learning resource generator. Generate REPLACEMENT study resources for a section.

SECTION: "${section.name}"
CHAPTER: "${chapter.name}"
MODULE: "${meta.title}", Audience="${meta.audience}", Difficulty="${meta.difficulty}", Tone="${meta.tone}"

The teacher REJECTED these resources and wants replacements:
${rejectedDescriptions}

${feedback ? `TEACHER'S FEEDBACK: ${feedback}` : "Please generate different, better alternatives."}

Return EXACT JSON:
{
  "resources": [
    {
      "name": string,
      "resourceType": "studyResource",
      "studyResourceTypeId": number,
      "studyResourceSubTypeId": number or null,
      "content": string or null,
      "url": string or null,
      "sortOrder": number
    }
  ]
}

AVAILABLE RESOURCE TYPES: ${JSON.stringify(resourceTypesForPrompt, null, 2)}

RULES:
- Type 6 (Article): MUST include "content". "url" can be null.
- Type 5 (Reference): MUST include "url".
- Type 32 (YouTube Video): MUST include "url" with a YouTube URL.
- Type 13 (E-Book): MUST include "url" with a link to a freely accessible e-book / PDF.
- Generate exactly ${rejected.length} replacement resource(s).
- IMPORTANT: Each replacement MUST use the SAME studyResourceTypeId as the resource it replaces. Do NOT change the resource type.
- Return ONLY valid JSON.`;

    try {
      const response = await ApiService.generateLMResources({ systemPrompt });

      const responseData = response.data as any;
      const resourceList = responseData.resources as any[];

      if (!resourceList || !Array.isArray(resourceList)) {
        throw new Error("Invalid resource structure");
      }

      const allReplacements: Resource[] = resourceList.map((r: any, idx: number) => ({
        name: r.name || `Replacement ${idx + 1}`,
        resourceType: (r.resourceType || "studyResource") as "studyResource" | "test",
        studyResourceTypeId: r.studyResourceTypeId,
        studyResourceSubTypeId: r.studyResourceSubTypeId || null,
        content: r.content || undefined,
        url: r.url || undefined,
        sortOrder: idx + 1,
      }));

      // Filter out replacements whose URL already exists in the module
      const existingUrls = collectExistingUrls(mod);
      const replacements = allReplacements.filter(r => {
        if (!r.url) return true;
        return !existingUrls.has(normalizeUrlForDedup(r.url));
      });

      // Merge: keep non-rejected resources, append replacements
      setAiGenerateModal(prev => {
        const kept = (prev.generatedResources || []).filter(
          r => !rejectedNames.includes(r.name)
        );
        // Also check against URLs of kept resources in the modal
        const keptUrls = new Set(kept.filter(r => r.url).map(r => normalizeUrlForDedup(r.url!)));
        const dedupedReplacements = replacements.filter(r => {
          if (!r.url) return true;
          return !keptUrls.has(normalizeUrlForDedup(r.url));
        });
        return {
          ...prev,
          generatedResources: [...kept, ...dedupedReplacements],
          isGenerating: false,
          generationError: null,
        };
      });
    } catch (error: any) {
      console.error("Error regenerating resources:", error);
      setAiGenerateModal(prev => ({
        ...prev,
        isGenerating: false,
        generationError: error.message || "Failed to generate replacements",
      }));
    }
  };

  // Confirm teacher-approved quiz questions — save to DB
  const handleConfirmQuestions = async (questions: AiQuizQuestion[], tags: Tag[]) => {
    const { chapterIndex, sectionIndex } = aiGenerateModal;
    if (!mod || chapterIndex === undefined || sectionIndex === undefined) return;

    const chapter = mod.groups[chapterIndex];
    const step = chapter?.steps[sectionIndex];
    if (!chapter || !step) return;

    setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null }));

    try {
      const response = await ApiService.aiCommitStepQuiz({
        questions,
        stepName: step.name,
        testDate: step.startDate || undefined,
        phaseLabel: aiGenerateModal.phaseName,
        bloomsTagTypeId: aiGenerateModal.quizBloomsEnabled ? iteachConfig.bloomsTagTypeId : undefined,
      });

      const { testId, testName, totalQuestions } = response.data.data;

      // Add the test as a new resource in the step
      const updatedMod = JSON.parse(JSON.stringify(mod)) as LearningModule;
      const targetStep = updatedMod.groups[chapterIndex].steps[sectionIndex];
      targetStep.resources.push({
        name: testName,
        resourceType: "test",
        testId: String(testId),
        testName: testName,
        sortOrder: targetStep.resources.length + 1,
        tags: tags.length > 0 ? tags : undefined,
      });
      setMod(updatedMod);
      setHasUnsavedChanges(true);

      closeAiGenerateModal();
      setFeedbackType("success");
      setFeedbackMessage(
        <>AI Quiz created with {totalQuestions} questions and linked to this section. <span style={{ color: "var(--status-danger)", fontWeight: 600 }}>Save the module to persist the link.</span></>,
      );
      setShowFeedbackModal(true);
    } catch (error: any) {
      console.error("Error committing AI quiz:", error);
      let errorMessage = "Failed to save quiz.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setAiGenerateModal(prev => ({
        ...prev,
        isGenerating: false,
        generationError: errorMessage,
      }));
    }
  };

  // Regenerate rejected quiz questions
  const regenerateRejectedQuestions = async (rejectedIndices: number[], feedback: string) => {
    const { chapterIndex, sectionIndex } = aiGenerateModal;
    if (!mod || chapterIndex === undefined || sectionIndex === undefined) return;

    setAiGenerateModal(prev => ({ ...prev, isGenerating: true, generationError: null }));

    const chapter = mod.groups[chapterIndex];
    const step = chapter?.steps[sectionIndex];
    if (!chapter || !step) return;

    const pdfUrls = step.resources
      .filter(r => r.resourceType === "studyResource" && (r.studyResourceTypeId === 13 || r.studyResourceTypeId === 1) && (r.url || r.fileUrl))
      .map(r => (r.url || r.fileUrl) as string);

    const articleIds = step.resources
      .filter(r => r.resourceType === "studyResource" && r.studyResourceTypeId === 6 && r.id)
      .map(r => r.id as number);

    // Build context with feedback about rejected questions
    const rejectedQuestions = (aiGenerateModal.generatedQuestions || [])
      .filter((_, i) => rejectedIndices.includes(i))
      .map(q => q.stemStatement);

    const replacementContext: Array<{ role: string; content: string }> = [
      {
        role: "user",
        content: `Replace ${rejectedIndices.length} rejected questions. Rejected questions were:\n${rejectedQuestions.map((q, i) => `${i + 1}. ${q}`).join("\n")}${feedback ? `\n\nTeacher's feedback: ${feedback}` : ""}`,
      },
    ];

    try {
      const response = await ApiService.aiPreviewStepQuiz({
        pdfUrls,
        articleIds: articleIds.length > 0 ? articleIds : undefined,
        stepName: step.name,
        chapterName: chapter.name,
        moduleName: mod.name || "",
        questionCount: rejectedIndices.length,
        testDate: step.startDate || undefined,
        bloomsTagTypeId: aiGenerateModal.quizBloomsEnabled ? iteachConfig.bloomsTagTypeId : undefined,
        conversationContext: replacementContext,
      });

      const { questions: newQuestions } = response.data.data;

      // Merge: keep approved, replace rejected with new ones
      const currentQuestions = aiGenerateModal.generatedQuestions || [];
      const kept = currentQuestions.filter((_, i) => !rejectedIndices.includes(i));
      const merged = [...kept, ...newQuestions];

      setAiGenerateModal(prev => ({
        ...prev,
        generatedQuestions: merged,
        isGenerating: false,
        generationError: null,
      }));
    } catch (error: any) {
      console.error("Error regenerating quiz questions:", error);
      setAiGenerateModal(prev => ({
        ...prev,
        isGenerating: false,
        generationError: error.message || "Failed to regenerate questions",
      }));
    }
  };

  // Group tags by type for organized display
  const tagsByType = useMemo(() => {
    const grouped: Record<string, { typeName: string; tags: Tag[] }> = {};
    availableTags.forEach(tag => {
      const typeId = tag.typeId?.toString() || 'untyped';
      if (!grouped[typeId]) {
        grouped[typeId] = {
          typeName: tag.typeName || 'Other',
          tags: []
        };
      }
      grouped[typeId].tags.push(tag);
    });
    return grouped;
  }, [availableTags]);

  // ================= View Mode Actions (Embed/Status/Query/Toggle) =================

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getEmbedStatusBadgeStyle = (status: string): React.CSSProperties => {
    const base: React.CSSProperties = { display: 'inline-block', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' };
    switch (status) {
      case 'completed': return { ...base, background: 'var(--status-success-bg)', color: 'var(--status-success-text)', border: '1px solid var(--status-success-border)' };
      case 'partial': return { ...base, background: 'var(--status-warning-bg)', color: 'var(--status-warning-text)', border: '1px solid var(--status-warning-border)' };
      case 'failed': return { ...base, background: 'var(--status-danger-bg)', color: 'var(--status-danger)', border: '1px solid var(--status-danger-border)' };
      case 'processing': return { ...base, background: 'var(--status-info-bg)', color: 'var(--brand-primary-active)', border: '1px solid #93c5fd' };
      default: return { ...base, background: 'var(--surface-sunken)', color: 'var(--text-body)', border: '1px solid var(--border-strong)' };
    }
  };

  const handleEmbed = async () => {
    if (!id || embeddingInProgress) return;
    try {
      setEmbeddingInProgress(true);
      await ApiService.embedLessonPlan(Number(id));
      setFeedbackType('success');
      setFeedbackMessage('RAGify started successfully! Open RAG Console to check progress.');
      setShowFeedbackModal(true);
    } catch (err: any) {
      console.error('Error starting embed:', err);
      setFeedbackType('error');
      setFeedbackMessage('Failed to start RAGify: ' + (err.message || 'Unknown error'));
      setShowFeedbackModal(true);
    } finally {
      setEmbeddingInProgress(false);
    }
  };

  const handleOpenRAGModal = async (tab: 'status' | 'search' = 'status') => {
    if (!id) return;
    setRagModalTab(tab);
    setShowRAGModal(true);
    // Always fetch status when opening (needed by both tabs for header info)
    if (!embedStatus) {
      setEmbedStatusLoading(true);
      setEmbedStatusError(null);
      setEmbedStatus(null);
      try {
        const response = await ApiService.getEmbededStatus(Number(id));
        setEmbedStatus(response.data as EmbedStatusResponse);
      } catch (err: any) {
        console.error('Error fetching embed status:', err);
        setEmbedStatusError(err.message || 'Failed to load embed status');
      } finally {
        setEmbedStatusLoading(false);
      }
    }
  };

  const handleRefreshRAGStatus = async () => {
    if (!id) return;
    setEmbedStatusLoading(true);
    setEmbedStatusError(null);
    try {
      const response = await ApiService.getEmbededStatus(Number(id));
      setEmbedStatus(response.data as EmbedStatusResponse);
    } catch (err: any) {
      console.error('Error fetching embed status:', err);
      setEmbedStatusError(err.message || 'Failed to load embed status');
    } finally {
      setEmbedStatusLoading(false);
    }
  };

  const handleSendQuery = async () => {
    if (!id || !queryPrompt.trim()) return;
    try {
      setQueryLoading(true);
      setQueryError(null);
      const response = await ApiService.queryLessonPlan({ prompt: queryPrompt.trim(), lessonPlanId: Number(id) });
      const responseData = response.data as any;
      const textResponse = responseData?.data || responseData?.message || responseData || '';
      setQueryResponse(typeof textResponse === 'string' ? textResponse : JSON.stringify(textResponse, null, 2));
    } catch (err: any) {
      console.error('Error querying lesson plan:', err);
      setQueryError(err.message || 'Failed to query lesson plan');
    } finally {
      setQueryLoading(false);
    }
  };

  const handleToggleModuleStatus = async () => {
    if (!id || !mod) return;
    const newStatus = mod.status === 1 ? 0 : 1;
    try {
      setTogglingStatus(true);
      await ApiService.updateLessonPlanStatus(Number(id), newStatus);
      setMod({ ...mod, status: newStatus });
      setShowToggleConfirm(false);
      setFeedbackType('success');
      setFeedbackMessage(`Module ${newStatus === 1 ? 'enabled' : 'disabled'} successfully.`);
      setShowFeedbackModal(true);
    } catch (err: any) {
      console.error('Error toggling status:', err);
      setFeedbackType('error');
      setFeedbackMessage('Failed to update status: ' + (err.message || 'Unknown error'));
      setShowFeedbackModal(true);
    } finally {
      setTogglingStatus(false);
    }
  };

  return (
    <>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <style>{styles}</style>
      <div className={`lmb-wrap${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <button
          className="lmb-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title="Toggle menu"
          style={{
            display: 'none',
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 999,
            background: 'var(--gradient-brand)',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 12px',
            cursor: 'pointer',
            color: 'white',
            fontSize: '1.5rem',
            lineHeight: 1
          }}
        >
          ☰
        </button>
        <h1 className="lmb-h1">
          {id && !loadingModule && mod?.name ? mod.name : 'Learning Module Builder'}
          <span className="lmb-tag">Chapters → Sections → Learning Objects</span>
          {isViewMode && <span className="lmb-tag" style={{ background: 'var(--status-success)' }}>VIEW MODE (Read-Only)</span>}
          {!isViewMode && id && <span className="lmb-tag" style={{ background: 'var(--brand-primary)' }}>EDITING</span>}
        </h1>

        {/* Back to List Button */}
        <div style={{ marginBottom: 16 }}>
          <button
            className="lmb-btn"
            onClick={() => history.push('/learning-modules')}
            style={{ marginRight: 8 }}
          >
            ← Back to List
          </button>
          {id && loadingModule && (
            <span className="lmb-muted" style={{ fontSize: 12 }}>
              Loading module...
            </span>
          )}
        </div>

        {/* Action Buttons Bar */}
        <div className="lmb-card" style={{ marginBottom: 16 }}>
          <div className="lmb-card-bd" style={{ padding: '12px 16px' }}>
            <div className="lmb-stack">
              {!isViewMode && mod && mod.groups.length > 0 && activeTab === 'builder' && (
                <button
                  className="lmb-btn"
                  onClick={analyzeModuleWithAI}
                  disabled={isAnalyzingModule}
                  style={{ background: 'var(--gradient-brand)', color: 'white', border: 'none' }}
                >
                  {isAnalyzingModule ? '⏳ Analyzing...' : '✨ Analyze with AI'}
                </button>
              )}
              {!isViewMode && activeTab !== 'issue' && activeTab !== 'batches' && (
                <button className="lmb-btn" onClick={newEmptyModule}>Reset/Clear</button>
              )}
              {!isViewMode && activeTab !== 'issue' && activeTab !== 'batches' && (
                <button
                  className="lmb-btn lmb-btn-primary"
                  onClick={saveLearningModule}
                  disabled={isSaving}
                  style={hasUnsavedChanges ? { background: 'var(--status-warning)', borderColor: 'var(--status-warning)' } : {}}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : hasUnsavedChanges ? '● Save Module (Unsaved Changes)' : 'Save Module'}
                </button>
              )}
              <button className="lmb-btn" onClick={exportJSON}>Export JSON</button>
              {!isViewMode && (
                <button className="lmb-btn" onClick={() => setShowExcelImport(true)}>Import Excel</button>
              )}
              {isViewMode && id && (
                <>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1.5px solid var(--brand-primary-subtle)', borderRadius: 8, padding: '4px 8px', background: 'var(--brand-primary-subtle)' }}>
                    <span
                      title={"RAG = Retrieval Augmented Generation\nIndexes this module's learning objects\nso AI can search and answer questions about them"}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: 'var(--brand-primary-subtle)', color: 'var(--brand-primary)', fontSize: 11, fontWeight: 700, cursor: 'help', flexShrink: 0 }}
                    >?</span>
                    <button
                      className="lmb-btn"
                      onClick={handleEmbed}
                      disabled={embeddingInProgress}
                      style={{ background: 'linear-gradient(135deg, var(--brand-accent) 0%, var(--brand-accent) 100%)', color: 'white', border: 'none' }}
                    >
                      {embeddingInProgress ? 'RAGifying...' : 'RAGify'}
                    </button>
                    <button
                      className="lmb-btn"
                      onClick={() => handleOpenRAGModal('status')}
                      style={{ background: 'linear-gradient(135deg, var(--status-success) 0%, var(--status-success) 100%)', color: 'white', border: 'none' }}
                    >
                      RAG Console
                    </button>
                  </div>
                  <button
                    className="lmb-btn"
                    onClick={() => setShowToggleConfirm(true)}
                    style={{
                      background: mod?.status === 1
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: 'white', border: 'none'
                    }}
                  >
                    {mod?.status === 1 ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    className="lmb-btn lmb-btn-primary"
                    onClick={() => history.push(`/lm-builder/${id}?mode=edit`)}
                  >
                    Switch to Edit Mode
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          hasUnsavedChanges={hasUnsavedChanges}
          isLessonPlan={mod?.lessonPlanType === 'lesson_plan'}
          hasModuleId={!!mod?.id}
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            module={mod}
            meta={meta}
            onMetaChange={updateMeta}
            onModuleChange={(updates) => mod && setMod({ ...mod, ...updates })}
            isViewMode={isViewMode}
            showAIHelper={showAIHelper}
            setShowAIHelper={setShowAIHelper}
            prompt={prompt}
            setPrompt={setPrompt}
            isGenerating={isGenerating}
            onGenerateTopics={generateTopicsFromAI}
            isEditMode={!!id}
          />
        )}

        {activeTab === 'table' && (
          <TableViewTab
            module={mod}
            onUpdateSection={updateSection}
            onRemoveSection={removeSection}
            onAddChapter={addChapter}
            onAddSection={addSection}
            onAddResource={addResource}
            onRemoveResource={removeResource}
            onUpdateResource={updateResource}
            availableTags={flipClassPhaseTags}
            resourceTypes={resourceTypes}
            isViewMode={isViewMode}
          />
        )}

        {activeTab === 'resources' && (
          <FlipClassBoardTab
            key={`resources-${mod?.id || 'new'}`}
            module={mod}
            setModule={setMod}
            availableTags={flipClassPhaseTags}
            resourceTypes={resourceTypes}
            onSave={saveLearningModule}
            isViewMode={isViewMode}
            onGenerateResources={generateResourcesForSection}
            onOpenAiGenerateModal={openAiGenerateModal}
            {...((lmConfig?.aiQuizEnabled ?? true) ? {
              onGenerateAiQuiz: generateAiQuiz,
              onBatchGenerateAiQuiz: batchGenerateAiQuiz,
            } : {})}
            aiLoadingStates={aiLoadingStates}
            aiConfiguredResourceIds={aiConfiguredResourceIds}
          />
        )}

        {activeTab === 'issue' && mod?.id && (
          <IssueTab
            module={mod}
            isViewMode={isViewMode}
          />
        )}

        {activeTab === 'batches' && mod?.lessonPlanType === 'lesson_plan' && (
          <BatchesTab
            module={mod}
            onModuleChange={(updates) => mod && setMod({ ...mod, ...updates })}
            isViewMode={isViewMode}
          />
        )}

        {/* Legacy/Original Content - Only show in builder tab */}
        <div style={{ display: activeTab === 'builder' ? 'block' : 'none' }}>
          {/* The original builder content follows below */}
        </div>

        {/* Legacy Metadata Card - Now Hidden */}
        <div style={{ display: 'none' }} className="lmb-card">
          <div className="lmb-card-hd">
            <div><strong>Module Metadata</strong></div>
          </div>
          <div className="lmb-card-bd">
            {/* Back to List Button */}
            <div style={{ marginBottom: 16 }}>
              <button
                className="lmb-btn"
                onClick={() => history.push('/learning-modules')}
                style={{ marginRight: 8 }}
              >
                ← Back to List
              </button>
              {id && loadingModule && (
                <span className="lmb-muted" style={{ fontSize: 12 }}>
                  Loading module...
                </span>
              )}
              {id && !loadingModule && mod?.name && (
                <span className="lmb-muted" style={{ fontSize: 12, color: "var(--status-success)" }}>
                  ✓ Editing: {mod.name}
                </span>
              )}
            </div>

            <div className="lmb-grid-3">
              <div>
                <label className="lmb-muted lmb-label">Title *</label>
                <input className="lmb-input" value={meta.title} onChange={(e) => updateMeta({ title: e.target.value })} placeholder="Enter module title..." disabled={isViewMode} />
              </div>
              <div>
                <label className="lmb-muted lmb-label">Audience</label>
                <input className="lmb-input" value={meta.audience} onChange={(e) => updateMeta({ audience: e.target.value })} placeholder="e.g., Developers, Students..." disabled={isViewMode} />
              </div>
              <div>
                <label className="lmb-muted lmb-label">Difficulty</label>
                <select className="lmb-select" value={meta.difficulty} onChange={(e) => updateMeta({ difficulty: e.target.value as ModuleMeta["difficulty"] })} disabled={isViewMode}>
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>
              <div>
                <label className="lmb-muted lmb-label">Estimated Duration (mins)</label>
                <input className="lmb-input" type="number" min={5} step={5} value={meta.durationMins} onChange={(e) => updateMeta({ durationMins: Math.max(5, Number(e.target.value) || 5) })} disabled={isViewMode} />
              </div>
              <div>
                <label className="lmb-muted lmb-label">Tone</label>
                <select className="lmb-select" value={meta.tone} onChange={(e) => updateMeta({ tone: e.target.value as ModuleMeta["tone"] })} disabled={isViewMode}>
                  <option>Practical</option>
                  <option>Friendly</option>
                  <option>Formal</option>
                  <option>Academic</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="lmb-muted lmb-label">Description / Remarks</label>
              <textarea
                className="lmb-textarea"
                value={mod?.remarks || ""}
                onChange={(e) => mod && setMod({ ...mod, remarks: e.target.value })}
                placeholder="Brief description of this learning module..."
                rows={2}
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>

        {/* Builder & JSON Row - Only show in builder tab */}
        <div className="lmb-row" style={{ display: activeTab === 'builder' ? 'flex' : 'none' }}>
          {/* Builder Panel */}
          <div className="lmb-builder-panel">
          <div className="lmb-card">
            <div className="lmb-card-hd">
              <div>Module Builder</div>
              <div className="lmb-stack">
                {mod && mod.groups.length > 0 && (
                  <>
                    <button className="lmb-btn lmb-btn-sm" onClick={expandAll}>Expand All</button>
                    <button className="lmb-btn lmb-btn-sm" onClick={collapseAll}>Collapse All</button>
                  </>
                )}
                {!isViewMode && <button className="lmb-btn-add" onClick={addChapter}>➕ Add Chapter</button>}
              </div>
            </div>
            <div className="lmb-card-bd">
              {mod && (
                <>
                  <div className="lmb-group" style={{ background: "var(--surface-page)" }}>
                    <div><b>Name:</b> {mod.name}</div>
                    <div><b>Code:</b> {mod.code}</div>
                    <div><b>Remarks:</b> {mod.remarks}</div>
                  </div>

                  {mod.groups.length === 0 && id && (
                    <div className="lmb-empty" style={{ padding: 20, background: "var(--status-warning-bg)", border: "1px solid var(--status-warning-border)" }}>
                      <strong>⚠️ Legacy Module Detected</strong>
                      <p style={{ marginTop: 8, marginBottom: 0 }}>
                        This module was created in an older system and doesn't have the Chapter/Section/Resource structure.
                        {!isViewMode && (
                          <span>
                            {" "}You can add new chapters and content using the "Add Chapter" button above, or use the AI Assistant to generate a complete structure.
                          </span>
                        )}
                      </p>
                    </div>
                  )}

                  {mod.groups.map((group, ci) => {
                    const isChapterExpanded = expandedChapters.has(ci);
                    return (
                    <div key={group.index} style={{ marginTop: 12 }}>
                      {/* Chapter Header - Clickable */}
                      <div
                        className="lmb-chapter-header"
                        style={{ borderLeft: `4px solid ${group.color}` }}
                        onClick={() => toggleChapter(ci)}
                      >
                        <span className={`lmb-chapter-toggle ${isChapterExpanded ? 'expanded' : ''}`}>▶</span>
                        <div className="lmb-chapter-title">
                          Chapter {ci + 1}: {group.name}
                        </div>
                        <span className="lmb-chapter-badge">{group.steps.length} section{group.steps.length !== 1 ? 's' : ''}</span>
                        {!isViewMode && (
                          <div className="lmb-stack" onClick={(e) => e.stopPropagation()}>
                            <button className="lmb-btn lmb-btn-sm" onClick={() => reorderChapter(ci, -1)}>↑</button>
                            <button className="lmb-btn lmb-btn-sm" onClick={() => reorderChapter(ci, 1)}>↓</button>
                            {(!group.steps || group.steps.length === 0) && <button className="lmb-btn lmb-btn-sm" onClick={() => removeChapter(ci)}>Remove</button>}
                          </div>
                        )}
                      </div>

                      {/* Chapter Content - Collapsible */}
                      {isChapterExpanded && (
                        <div className="lmb-chapter-content" style={{ borderLeftColor: group.color }}>
                          <div className="lmb-group" style={{ borderLeft: `4px solid ${group.color}` }}>
                            <div className="lmb-grid-2">
                              <div>
                                <label className="lmb-muted lmb-label">Chapter Name</label>
                                <input className="lmb-input" value={group.name} onChange={(e) => updateChapter(ci, { name: e.target.value })} disabled={isViewMode} />
                              </div>
                              <div>
                                <label className="lmb-muted lmb-label">Color</label>
                                <input className="lmb-input" type="color" value={group.color} onChange={(e) => updateChapter(ci, { color: e.target.value })} disabled={isViewMode} />
                              </div>
                            </div>

                      <div className="lmb-space-between" style={{ marginTop: 8 }}>
                        <div className="lmb-muted">Sections</div>
                        {!isViewMode && <button className="lmb-btn-add" onClick={() => addSection(ci)}>➕ Add Section</button>}
                      </div>

                      {group.steps.length === 0 ? (
                        <div className="lmb-empty" style={{ marginTop: 8 }}>No sections yet. Add one.</div>
                      ) : (
                        group.steps.map((step, si) => {
                          const isSectionExpanded = expandedSections.has(`${ci}-${si}`);
                          return (
                          <div key={`${ci}-${si}`} style={{ marginTop: 8 }}>
                            {/* Section Header - Clickable */}
                            <div
                              className="lmb-section-header"
                              style={{ borderLeftColor: group.color }}
                              onClick={() => toggleSection(ci, si)}
                            >
                              <span className={`lmb-section-toggle ${isSectionExpanded ? 'expanded' : ''}`}>▶</span>
                              <div className="lmb-section-title">
                                {step.name}
                              </div>
                              <span className="lmb-section-badge">{step.resources.length} resource{step.resources.length !== 1 ? 's' : ''}</span>
                              {!isViewMode && (
                                <div className="lmb-stack" onClick={(e) => e.stopPropagation()}>
                                  <button className="lmb-btn lmb-btn-sm" onClick={() => reorderSection(ci, si, -1)}>↑</button>
                                  <button className="lmb-btn lmb-btn-sm" onClick={() => reorderSection(ci, si, 1)}>↓</button>
                                  {(!step.resources || step.resources.length === 0) && <button className="lmb-btn lmb-btn-sm" onClick={() => removeSection(ci, si)}>Remove</button>}
                                </div>
                              )}
                            </div>

                            {/* Section Content - Collapsible */}
                            {isSectionExpanded && (
                              <div className="lmb-section-content">
                                {!isViewMode && (
                                  <div style={{ marginBottom: 12, paddingLeft: 8 }}>
                                    <input
                                      className="lmb-input"
                                      value={step.name}
                                      onChange={(e) => updateSection(ci, si, { name: e.target.value })}
                                      placeholder="Section name..."
                                      style={{ fontSize: 13 }}
                                    />
                                  </div>
                                )}

                            <div className="lmb-space-between" style={{ marginBottom: 12, paddingLeft: 8 }}>
                              <div className="lmb-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px' }}>RESOURCES</div>
                              {!isViewMode && <button className="lmb-btn-add" onClick={() => addResource(ci, si)}>➕ Add Resource</button>}
                            </div>

                            {step.resources.length === 0 ? (
                              <div className="lmb-empty" style={{ marginTop: 8, padding: 12, fontSize: 13 }}>No resources yet.</div>
                            ) : (
                              step.resources.map((resource, ri) => {
                                const requiredFields = getRequiredFields(resource.studyResourceTypeId);
                                return (
                                  <div key={`${ci}-${si}-${ri}`} className="lmb-resource-item">
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                      <div style={{ flex: '1 1 250px', minWidth: 200 }}>
                                        <label className="lmb-muted lmb-label">Resource Name</label>
                                        <input
                                          className="lmb-input"
                                          value={resource.name}
                                          onChange={(e) => updateResource(ci, si, ri, { name: e.target.value })}
                                          placeholder="Resource name..."
                                          required={requiredFields.requiresName}
                                          disabled={isViewMode}
                                          style={{ fontSize: 13 }}
                                        />
                                      </div>
                                      <div style={{ flex: '0 0 130px', minWidth: 130 }}>
                                        <label className="lmb-muted lmb-label">Resource Type</label>
                                        <select
                                          className="lmb-select"
                                          value={resource.resourceType || "studyResource"}
                                          onChange={(e) => {
                                            const newResourceType = e.target.value as "studyResource" | "test";
                                            updateResource(ci, si, ri, {
                                              resourceType: newResourceType,
                                              // Clear type-specific fields when switching
                                              ...(newResourceType === "test" ? {
                                                studyResourceTypeId: undefined,
                                                studyResourceSubTypeId: undefined,
                                                content: undefined,
                                                fileUrl: undefined,
                                                url: undefined,
                                              } : {
                                                testId: undefined,
                                                testName: undefined,
                                              }),
                                            });
                                          }}
                                          disabled={isViewMode}
                                          style={{ fontSize: 13 }}
                                        >
                                          <option value="studyResource">Study Resource</option>
                                          <option value="test">Test</option>
                                        </select>
                                      </div>
                                      {resource.resourceType === "test" ? (
                                        <div style={{ flex: '1 1 200px', minWidth: 150 }}>
                                          <label className="lmb-muted lmb-label">Test ID</label>
                                          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <input
                                              className="lmb-input"
                                              value={resource.testId || ""}
                                              onChange={(e) => updateResource(ci, si, ri, { testId: e.target.value })}
                                              placeholder="Enter test ID..."
                                              disabled={isViewMode}
                                              style={{ fontSize: 13, flex: 1 }}
                                            />
                                            {!isViewMode && (
                                              <button
                                                className="lmb-btn lmb-btn-primary"
                                                onClick={() => setQuizModalKey(`${ci}-${si}-${ri}`)}
                                                type="button"
                                                style={{ whiteSpace: "nowrap", fontSize: 12, padding: "4px 10px" }}
                                              >
                                                Create Test
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div style={{ flex: '1 1 200px', minWidth: 150 }}>
                                            <label className="lmb-muted lmb-label">Type</label>
                                            {loadingResourceTypes ? (
                                              <input className="lmb-input" disabled value="Loading..." style={{ fontSize: 13 }} />
                                            ) : (
                                              <select
                                                className="lmb-select"
                                                value={resource.studyResourceTypeId}
                                                onChange={(e) => {
                                                  const newTypeId = Number(e.target.value);
                                                  const newType = resourceTypes.find(t => t.id === newTypeId);
                                                  const subtypes = newType?.subtypes || [];
                                                  // Auto-select first subtype if available
                                                  const autoSubTypeId = subtypes.length > 0 ? subtypes[0].id : undefined;
                                                  updateResource(ci, si, ri, {
                                                    studyResourceTypeId: newTypeId,
                                                    studyResourceSubTypeId: autoSubTypeId
                                                  });
                                                }}
                                                required
                                                disabled={isViewMode}
                                                style={{ fontSize: 13 }}
                                              >
                                                <option value="">Type...</option>
                                                {resourceTypes.map(type => (
                                                  <option key={type.id} value={type.id}>{type.name}</option>
                                                ))}
                                              </select>
                                            )}
                                          </div>
                                          {/* SubType dropdown - only show if selected type has subtypes */}
                                          {(() => {
                                            const currentType = resourceTypes.find(t => t.id === resource.studyResourceTypeId);
                                            const availableSubtypes = currentType?.subtypes || [];
                                            if (availableSubtypes.length === 0) return null;
                                            return (
                                              <div style={{ flex: '1 1 180px', minWidth: 140 }}>
                                                <label className="lmb-muted lmb-label">Sub-Type</label>
                                                <select
                                                  className="lmb-select"
                                                  value={resource.studyResourceSubTypeId || ""}
                                                  onChange={(e) => {
                                                    updateResource(ci, si, ri, {
                                                      studyResourceSubTypeId: e.target.value ? Number(e.target.value) : undefined
                                                    });
                                                  }}
                                                  disabled={isViewMode}
                                                  style={{ fontSize: 13 }}
                                                >
                                                  <option value="">Select sub-type...</option>
                                                  {availableSubtypes.map(subtype => (
                                                    <option key={subtype.id} value={subtype.id}>{subtype.name}</option>
                                                  ))}
                                                </select>
                                              </div>
                                            );
                                          })()}
                                        </>
                                      )}
                                      {Object.keys(tagsByType).length > 0 && (
                                        <div style={{ flex: '1 1 250px', minWidth: 200 }}>
                                          {Object.entries(tagsByType).map(([typeId, typeData]) => (
                                            <div key={typeId} style={{ marginBottom: 8 }}>
                                              <label className="lmb-muted lmb-label">{typeData.typeName}</label>
                                              <TagMultiSelect
                                                selectedTags={(resource.tags || []).filter(tag => tag.typeId?.toString() === typeId)}
                                                availableTags={typeData.tags}
                                                onChange={(selectedTagsForType) => {
                                                  const otherTypeTags = (resource.tags || []).filter(tag => tag.typeId?.toString() !== typeId);
                                                  const allTags = [...otherTypeTags, ...selectedTagsForType];
                                                  updateResource(ci, si, ri, { tags: allTags });
                                                }}
                                                placeholder={`Select...`}
                                                disabled={loadingTags || isViewMode}
                                              />
                                              {(loadingTags || isViewMode) && (
                                                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
                                                  {loadingTags ? '⏳ Loading tags...' : '🔒 View mode'}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                          {!isViewMode && resource.name && (
                                            <button
                                              className="lmb-btn lmb-btn-sm"
                                              onClick={() => suggestTagsForResource(ci, si, ri)}
                                              disabled={aiLoadingStates[`tags-${ci}-${si}-${ri}`]}
                                              style={{ marginTop: 8, background: 'var(--brand-primary)', color: 'white', border: 'none', width: '100%' }}
                                            >
                                              {aiLoadingStates[`tags-${ci}-${si}-${ri}`] ? '⏳ Suggesting...' : '🤖 Suggest All Tags with AI'}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Test Name field - only show for test resources */}
                                    {resource.resourceType === "test" && (
                                      <div style={{ marginTop: 12 }}>
                                        <label className="lmb-muted lmb-label">Test Name (Optional)</label>
                                        <input
                                          className="lmb-input"
                                          value={resource.testName || ""}
                                          onChange={(e) => updateResource(ci, si, ri, { testName: e.target.value })}
                                          placeholder="Enter test name for display..."
                                          disabled={isViewMode}
                                          style={{ fontSize: 13 }}
                                        />
                                      </div>
                                    )}

                                    {/* Content/URL section - Full width below - only for Study Resources */}
                                    {resource.resourceType !== "test" && (
                                    <div style={{ marginTop: 12 }}>
                                        {requiredFields.requiresContent && (
                                          <div>
                                            <textarea
                                              className="lmb-textarea"
                                              value={resource.content}
                                              onChange={(e) => updateResource(ci, si, ri, { content: e.target.value })}
                                              rows={3}
                                              placeholder="Content *"
                                              required
                                              disabled={isViewMode}
                                              style={{ fontSize: 13 }}
                                            />
                                            {!isViewMode && resource.name && (
                                              <button
                                                className="lmb-btn lmb-btn-sm"
                                                onClick={() => openAiGenerateModal("content", { chapterIndex: ci, sectionIndex: si, resourceIndex: ri })}
                                                disabled={aiLoadingStates[`content-${ci}-${si}-${ri}`]}
                                                style={{ marginTop: 8, background: 'var(--brand-primary)', color: 'white', border: 'none' }}
                                              >
                                                {aiLoadingStates[`content-${ci}-${si}-${ri}`] ? '⏳ Generating...' : '✨ Generate Content with AI'}
                                              </button>
                                            )}
                                          </div>
                                        )}

                                        {requiredFields.requiresUrl && (
                                          <div>
                                            <input
                                              className="lmb-input"
                                              type="url"
                                              value={resource.url || ""}
                                              onChange={(e) => {
                                                const newUrl = e.target.value;
                                                updateResource(ci, si, ri, { url: newUrl });
                                                // Auto-extract metadata when URL is pasted
                                                if (newUrl && (newUrl.includes('youtube.com') || newUrl.includes('youtu.be') || newUrl.startsWith('http'))) {
                                                  extractUrlMetadata(ci, si, ri, newUrl);
                                                }
                                              }}
                                              onBlur={(e) => {
                                                // Also try on blur for manually typed URLs
                                                const url = e.target.value;
                                                if (url && !resource.name.includes(url)) {
                                                  extractUrlMetadata(ci, si, ri, url);
                                                }
                                              }}
                                              placeholder="URL * (metadata will be auto-extracted)"
                                              required
                                              disabled={isViewMode}
                                              style={{ fontSize: 13 }}
                                            />
                                            {aiLoadingStates[`url-${ci}-${si}-${ri}`] && (
                                              <div className="lmb-muted" style={{ marginTop: 4, fontSize: 11 }}>
                                                ⏳ Extracting metadata...
                                              </div>
                                            )}
                                            <YouTubePreview url={resource.url} />
                                          </div>
                                        )}

                                    {requiredFields.attachFromLibrary && (
                                      <div style={{ marginTop: 8 }}>
                                        <label className="lmb-muted lmb-label">
                                          SCORM Course from Library<span style={{ color: "red" }}> *</span>
                                        </label>
                                        <select
                                          className="lmb-input"
                                          value={resource.id || ""}
                                          onChange={(e) => {
                                            const courseId = Number(e.target.value);
                                            const course = scormLibrary.find(c => c.id === courseId);
                                            if (course) {
                                              updateResource(ci, si, ri, { id: course.id, name: course.name });
                                            }
                                          }}
                                          disabled={isViewMode}
                                          style={{ fontSize: 13 }}
                                        >
                                          <option value="">
                                            {scormLibraryLoaded
                                              ? (scormLibrary.length ? "Select a SCORM course…" : "No SCORM courses in the library yet")
                                              : "Loading SCORM library…"}
                                          </option>
                                          {scormLibrary.map(c => (
                                            <option key={c.id} value={c.id}>
                                              {c.name}{c.version ? ` (v${c.version})` : ""}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="lmb-muted" style={{ marginTop: 4, fontSize: 11 }}>
                                          Courses are uploaded and managed in the{" "}
                                          <a href="/scorm-courses" target="_blank" rel="noopener noreferrer">SCORM Course Library</a>.
                                          Attaching links the library course — it is not copied.
                                        </div>
                                      </div>
                                    )}

                                    {requiredFields.requiresFile && (
                                      <div style={{ marginTop: 8 }}>
                                        <label className="lmb-muted lmb-label">
                                          Upload File<span style={{ color: "red" }}> *</span>
                                        </label>
                                        <input
                                          className="lmb-input"
                                          type="file"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                              const fileKey = `resource_${ci}_${si}_${ri}`;
                                              updateResource(ci, si, ri, { file, fileUrl: file.name, fileKey });
                                            }
                                          }}
                                          required={!resource.fileUrl}
                                          disabled={isViewMode}
                                        />
                                        {resource.fileUrl && (
                                          <div className="lmb-muted" style={{ marginTop: 4, fontSize: 12 }}>
                                            Current file: {resource.fileUrl}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {requiredFields.allowFileOrUrl && (
                                      <div style={{ marginTop: 8 }}>
                                        <label className="lmb-muted lmb-label">
                                          Upload File OR Enter URL<span style={{ color: "red" }}> *</span>
                                        </label>
                                        <input
                                          className="lmb-input"
                                          type="file"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                              const fileKey = `resource_${ci}_${si}_${ri}`;
                                              updateResource(ci, si, ri, { file, fileUrl: file.name, fileKey, url: "" });
                                            }
                                          }}
                                          style={{ marginBottom: 8 }}
                                          disabled={isViewMode}
                                        />
                                        <div className="lmb-muted" style={{ textAlign: "center", margin: "8px 0", fontSize: 12 }}>OR</div>
                                        <input
                                          className="lmb-input"
                                          type="url"
                                          value={resource.url || ""}
                                          onChange={(e) => updateResource(ci, si, ri, { url: e.target.value, fileUrl: "", file: undefined, fileKey: undefined })}
                                          placeholder="https://example.com"
                                          disabled={isViewMode}
                                        />
                                        {(resource.fileUrl || resource.url) && (
                                          <div className="lmb-muted" style={{ marginTop: 4, fontSize: 12 }}>
                                            Current: {resource.fileUrl ? `File: ${resource.fileUrl}` : `URL: ${resource.url}`}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Show content field for types that don't have specific requirements */}
                                    {!requiredFields.requiresContent && !requiredFields.requiresUrl && !requiredFields.requiresFile && !requiredFields.allowFileOrUrl && !requiredFields.attachFromLibrary && (
                                      <div style={{ marginTop: 8 }}>
                                        <label className="lmb-muted lmb-label">Content (Optional)</label>
                                        <textarea
                                          className="lmb-textarea"
                                          value={resource.content}
                                          onChange={(e) => updateResource(ci, si, ri, { content: e.target.value })}
                                          rows={3}
                                        />
                                      </div>
                                    )}
                                    </div>
                                    )}

                                    {!isViewMode && !resource.id && (
                                      <button
                                        className="lmb-btn lmb-btn-sm"
                                        onClick={() => removeResource(ci, si, ri)}
                                        style={{ marginTop: 8 }}
                                      >
                                        Remove Resource
                                      </button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            )}
          </div>
          );
        })}
                </>
              )}
            </div>
          </div>
          </div>

          {/* JSON Preview Panel (Fixed Right Side) */}
          <div
            className={`lmb-json-panel ${isJsonCollapsed ? 'collapsed' : ''}`}
            style={{ width: jsonPanelWidth }}
          >
            <div
              className={`lmb-resizer ${isDragging ? 'dragging' : ''}`}
              onMouseDown={() => setIsDragging(true)}
            />
            <div className="lmb-json-header">
              <div style={{ fontWeight: 600, fontSize: 15 }}>JSON Preview</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="lmb-btn lmb-btn-sm" onClick={exportJSON}>Download</button>
                <button
                  className="lmb-btn lmb-btn-sm"
                  onClick={() => setIsJsonCollapsed(true)}
                  style={{ fontSize: 18, padding: '4px 8px' }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="lmb-json-body">
              <pre className="lmb-json">{jsonPreview || " "}</pre>
            </div>
          </div>

          {/* JSON Toggle Button (shows when panel is collapsed) */}
          {isJsonCollapsed && (
            <button
              className="lmb-json-toggle"
              onClick={() => setIsJsonCollapsed(false)}
              title="Show JSON Preview"
            >
              {'{ }'}
            </button>
          )}
        </div>
      </div>

      {/* Save Result Modal */}
      <Modal show={showSaveModal} onHide={handleCloseSaveModal} centered dialogClassName="save-result-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {saveModalType === 'success' ? (
              <span style={{ color: 'var(--status-success)' }}>✓ {id ? 'Module Updated' : 'Module Created'}</span>
            ) : (
              <span style={{ color: 'var(--status-danger)' }}>✗ Error</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveModalType === 'success' && saveModalData ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px', color: 'var(--text-strong)' }}>
                {saveModalData.title}
              </p>
              <div style={{ background: 'var(--surface-page)', borderRadius: '10px', padding: '16px 24px', textAlign: 'left' }}>
                {[
                  { icon: '📚', label: 'Chapters', value: saveModalData.chapters },
                  { icon: '📄', label: 'Sections', value: saveModalData.sections },
                ].map((stat, i) => (
                  <div key={stat.label}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'var(--text-body)' }}>
                        <span style={{ fontSize: '1.15rem' }}>{stat.icon}</span>
                        {stat.label}
                      </span>
                      <span style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-strong)' }}>{stat.value}</span>
                    </div>
                    <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--border-default)' }} />
                  </div>
                ))}
                {/* Resource breakdown by type */}
                {saveModalData.resourceBreakdown.length > 0 ? (
                  <>
                    <div style={{ padding: '6px 0 4px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Resources ({saveModalData.resources})
                    </div>
                    {saveModalData.resourceBreakdown.map((stat, i, arr) => (
                      <div key={stat.label}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0 8px 12px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                            <span style={{ fontSize: '1rem' }}>{stat.icon}</span>
                            {stat.label}
                          </span>
                          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-body)' }}>{stat.value}</span>
                        </div>
                        {i < arr.length - 1 && <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--border-subtle)' }} />}
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: 'var(--text-body)' }}>
                      <span style={{ fontSize: '1.15rem' }}>📎</span>
                      Resources
                    </span>
                    <span style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-strong)' }}>{saveModalData.resources}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: '1.1rem', margin: 0, whiteSpace: 'pre-wrap' }}>{saveModalMessage}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={saveModalType === 'success' ? 'success' : 'danger'} onClick={handleCloseSaveModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* AI Generation Feedback Modal */}
      <Modal show={showAiModal} onHide={() => setShowAiModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {aiModalType === 'success' ? (
              <span style={{ color: 'var(--status-success)' }}>Topics Generated</span>
            ) : (
              <span style={{ color: 'var(--status-danger)' }}>Error</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '1rem', margin: 0 }}>{aiModalMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={aiModalType === 'success' ? 'primary' : 'danger'} onClick={() => {
            setShowAiModal(false);
            if (aiModalType === 'success') setActiveTab('builder');
          }}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Quiz Creation Modal */}
      <Modal
        show={quizModalKey !== null}
        onHide={() => setQuizModalKey(null)}
        fullscreen
        dialogClassName="lmb-quiz-creation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Test</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto" }}>
          {quizModalKey !== null && (
            <QuizFormPanel
              onCreated={(result) => {
                // Parse the key "ci-si-ri" to update the correct resource
                const [ci, si, ri] = quizModalKey.split("-").map(Number);
                updateResource(ci, si, ri, {
                  testId: String(result.testId),
                  testName: result.name,
                });
                setQuizModalKey(null);
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Excel Import Modal */}
      <ExcelImportModal
        show={showExcelImport}
        onClose={() => setShowExcelImport(false)}
        onImport={handleExcelImport}
        existingGroupCount={mod?.groups.length || 0}
      />

      {/* Batch Quiz Progress Modal */}
      <Modal
        show={batchQuizProgress !== null && !batchQuizProgress.done}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Generating AI Quizzes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {batchQuizProgress && (
            <div>
              <p style={{ margin: "0 0 12px", fontWeight: 600 }}>
                Processing step {batchQuizProgress.current} of {batchQuizProgress.total}
              </p>
              <p style={{ margin: "0 0 12px", color: "var(--text-muted)", fontSize: 13 }}>
                {batchQuizProgress.currentStepName}
              </p>
              <div style={{
                height: 8, background: "var(--surface-sunken)", borderRadius: 4,
                overflow: "hidden", marginBottom: 12,
              }}>
                <div style={{
                  height: "100%",
                  width: `${batchQuizProgress.total > 0 ? Math.round((batchQuizProgress.current / batchQuizProgress.total) * 100) : 0}%`,
                  background: "var(--gradient-brand)",
                  borderRadius: 4, transition: "width 0.3s ease",
                }} />
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 13, color: "var(--text-muted)" }}>
                <span style={{ color: "var(--status-success)" }}>Created: {batchQuizProgress.created}</span>
                <span>Skipped: {batchQuizProgress.skipped}</span>
                {batchQuizProgress.failed > 0 && (
                  <span style={{ color: "var(--status-danger)" }}>Failed: {batchQuizProgress.failed}</span>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Feedback Modal (for embed/toggle results) */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {feedbackType === 'success' ? <span style={{ color: 'var(--status-success)' }}>Success</span> : <span style={{ color: 'var(--status-danger)' }}>Error</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p style={{ margin: 0 }}>{feedbackMessage}</p></Modal.Body>
        <Modal.Footer>
          <Button variant={feedbackType === 'success' ? 'success' : 'danger'} onClick={() => setShowFeedbackModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>

      {/* Toggle Status Confirmation Modal */}
      <Modal show={showToggleConfirm} onHide={() => setShowToggleConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm {mod?.status === 1 ? 'Disable' : 'Enable'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0 }}>Are you sure you want to {mod?.status === 1 ? 'disable' : 'enable'} "{mod?.name}"?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowToggleConfirm(false)}>Cancel</Button>
          <Button variant={mod?.status === 1 ? 'danger' : 'success'} onClick={handleToggleModuleStatus} disabled={togglingStatus}>
            {togglingStatus ? 'Updating...' : mod?.status === 1 ? 'Disable' : 'Enable'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Combined RAG Console Modal */}
      <Modal show={showRAGModal} onHide={() => { setShowRAGModal(false); setEmbedStatus(null); }} fullscreen>
        <Modal.Header closeButton style={{ background: 'var(--surface-page)', borderBottom: '1px solid var(--border-default)', padding: '14px 24px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>RAG Console</span>
              {embedStatus?.data && (
                <>
                  <span style={{ color: 'var(--text-faint)' }}>|</span>
                  <span style={getEmbedStatusBadgeStyle(embedStatus.data.status)}>{embedStatus.data.status.toUpperCase()}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {embedStatus.data.processedResources}/{embedStatus.data.totalResources} resources
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {embedStatus.data.totalChunks} chunks &middot; {embedStatus.data.totalVectors} vectors
                  </span>
                  {embedStatus.data.processingCompletedAt && (
                    <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>
                      &middot; Last indexed {formatDateTime(embedStatus.data.processingCompletedAt)}
                    </span>
                  )}
                </>
              )}
            </div>
            <Modal.Title style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-strong)', lineHeight: 1.3 }}>{mod?.name}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '2px solid var(--border-default)', padding: '0 24px', background: 'var(--surface-card)', flexShrink: 0 }}>
            {([
              { key: 'status' as const, label: 'Status & Resources' },
              { key: 'search' as const, label: 'Search' },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setRagModalTab(tab.key)}
                style={{
                  padding: '12px 20px', fontSize: '14px', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
                  color: ragModalTab === tab.key ? '#059669' : '#6b7280',
                  borderBottom: ragModalTab === tab.key ? '2px solid #059669' : '2px solid transparent',
                  marginBottom: '-2px', transition: 'all 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
            {/* ═══ STATUS TAB ═══ */}
            {ragModalTab === 'status' && (
              <>
                {embedStatusLoading && <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>Loading RAG status...</div>}
                {embedStatusError && (
                  <div style={{ padding: '16px', background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-border)', borderRadius: '8px', color: 'var(--status-danger)' }}>
                    <strong>Error:</strong> {embedStatusError}
                  </div>
                )}
                {!embedStatusLoading && !embedStatusError && embedStatus && (
                  <>
                    {!embedStatus.isRAGified && !embedStatus.data && (
                      <div style={{ textAlign: 'center', padding: '40px 20px', background: 'var(--status-warning-bg)', border: '1px solid var(--status-warning-border)', borderRadius: '8px', color: 'var(--status-warning-text)' }}>
                        <h4 style={{ margin: '0 0 8px 0' }}>Not RAGified Yet</h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>{embedStatus.message || 'This module has not been RAGified yet.'}</p>
                        <button
                          onClick={async () => {
                            await handleEmbed();
                            handleRefreshRAGStatus();
                          }}
                          disabled={embeddingInProgress}
                          style={{
                            background: 'linear-gradient(135deg, var(--brand-accent) 0%, var(--brand-accent) 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: embeddingInProgress ? 'not-allowed' : 'pointer',
                            opacity: embeddingInProgress ? 0.6 : 1,
                          }}
                        >
                          {embeddingInProgress ? 'RAGifying...' : 'RAGify Now'}
                        </button>
                      </div>
                    )}
                    {embedStatus.data && (
                      <>
                        {embedStatus.data.errorSummary && (
                          <div style={{ marginBottom: '20px', padding: '12px 16px', background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-border)', borderRadius: '8px', color: 'var(--status-danger)', fontSize: '13px', maxHeight: '200px', overflowY: 'auto' }}>
                            <strong style={{ display: 'block', marginBottom: '8px' }}>Error Summary:</strong>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: 'inherit' }}>{embedStatus.data.errorSummary}</pre>
                          </div>
                        )}

                        {/* Resources list */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h5 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text-strong)' }}>Resources ({embedStatus.data.resources.length})</h5>
                          <button
                            onClick={handleRefreshRAGStatus}
                            disabled={embedStatusLoading}
                            style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', cursor: embedStatusLoading ? 'not-allowed' : 'pointer', opacity: embedStatusLoading ? 0.5 : 1 }}
                          >
                            {embedStatusLoading ? 'Refreshing...' : 'Refresh'}
                          </button>
                        </div>

                        {/* Resource cards — better use of space than a cramped table */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {embedStatus.data.resources.map((resource) => (
                            <div
                              key={resource.studyResourceId}
                              style={{
                                border: '1px solid var(--border-default)', borderRadius: '8px', padding: '14px 16px',
                                background: resource.status === 'failed' ? '#fef8f8' : resource.status === 'completed' ? '#f0fdf4' : '#fff',
                                borderLeft: `3px solid ${resource.status === 'completed' ? '#10b981' : resource.status === 'failed' ? '#ef4444' : resource.status === 'processing' ? '#3b82f6' : '#d1d5db'}`,
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-strong)' }}>{resource.resourceName || 'Unnamed Resource'}</span>
                                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: 'var(--surface-sunken)', color: 'var(--text-muted)', fontWeight: 500, flexShrink: 0 }}>{resource.resourceType}</span>
                                  </div>
                                  {resource.resourceUrl && (
                                    <a href={resource.resourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--brand-primary)', textDecoration: 'none' }}>
                                      View source
                                    </a>
                                  )}
                                </div>
                                <span style={getEmbedStatusBadgeStyle(resource.status)}>{resource.status}</span>
                              </div>
                              {/* Details row */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                                {resource.status === 'completed' && (
                                  <>
                                    <span>{resource.chunksCreated} chunks</span>
                                    <span>{resource.vectorsStored} vectors</span>
                                    {resource.processingTimeMs > 0 && <span>{(resource.processingTimeMs / 1000).toFixed(1)}s</span>}
                                  </>
                                )}
                                {resource.fileSizeBytes != null && resource.fileSizeBytes > 0 && (
                                  <span>{(resource.fileSizeBytes / 1024).toFixed(0)} KB</span>
                                )}
                                {resource.processedAt && (
                                  <span>Processed: {formatDateTime(resource.processedAt)}</span>
                                )}
                              </div>
                              {/* Error message */}
                              {resource.status === 'failed' && resource.errorMessage && (
                                <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-border)', borderRadius: '4px', fontSize: '12px', color: 'var(--status-danger)', lineHeight: 1.4 }}>
                                  {resource.errorMessage}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* ═══ SEARCH TAB ═══ */}
            {ragModalTab === 'search' && (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Query Input */}
                <div style={{ marginBottom: '16px', flexShrink: 0 }}>
                  <textarea
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-strong)', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                    value={queryPrompt}
                    onChange={(e) => setQueryPrompt(e.target.value)}
                    placeholder="Type your question here... e.g., Explain about CUTTING TOOL MATERIALS"
                    rows={3}
                    disabled={queryLoading}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendQuery(); } }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Press Enter to search, Shift+Enter for new line</span>
                    <button
                      onClick={handleSendQuery}
                      disabled={queryLoading || !queryPrompt.trim()}
                      style={{
                        background: 'linear-gradient(135deg, var(--status-success) 0%, var(--status-success) 100%)', border: 'none', padding: '8px 20px', borderRadius: '6px',
                        fontSize: '13px', fontWeight: 600, cursor: queryLoading || !queryPrompt.trim() ? 'not-allowed' : 'pointer',
                        color: 'white', opacity: queryLoading || !queryPrompt.trim() ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px'
                      }}
                    >
                      {queryLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </div>

                {queryError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-border)', borderRadius: '8px', color: 'var(--status-danger)', marginBottom: '16px' }}>
                    {queryError}
                  </div>
                )}

                {/* Loading Skeleton */}
                {queryLoading && !queryResponse && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '10px 16px', background: 'var(--surface-sunken)', borderRadius: '10px 10px 0 0', border: '1px solid var(--border-default)', borderBottom: 'none' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Results</span>
                    </div>
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: '0 0 10px 10px', padding: '24px', flex: 1 }}>
                      {[100, 75, 100, 45, 75].map((w, i) => (
                        <div key={i} style={{ height: '14px', width: `${w}%`, background: 'var(--surface-sunken)', borderRadius: '6px', marginBottom: '14px', animation: 'pulse 1.5s infinite' }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Query Response */}
                {queryResponse && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ padding: '10px 16px', background: 'var(--surface-sunken)', borderRadius: '10px 10px 0 0', border: '1px solid var(--border-default)', borderBottom: 'none' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Results</span>
                    </div>
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: '0 0 10px 10px', padding: '24px 28px', flex: 1, overflowY: 'auto', fontSize: '14px', lineHeight: 1.7 }}>
                      <ReactMarkdown
                        components={{
                          h1: ({ node, ...props }) => <h1 style={{ fontSize: '20px', marginTop: '20px', marginBottom: '10px', fontWeight: 600, color: 'var(--text-strong)' }} {...props} />,
                          h2: ({ node, ...props }) => <h2 style={{ fontSize: '18px', marginTop: '18px', marginBottom: '8px', fontWeight: 600, color: 'var(--text-strong)' }} {...props} />,
                          h3: ({ node, ...props }) => <h3 style={{ fontSize: '16px', marginTop: '14px', marginBottom: '6px', fontWeight: 600, color: 'var(--text-strong)' }} {...props} />,
                          p: ({ node, ...props }) => <p style={{ margin: '10px 0', lineHeight: '1.7' }} {...props} />,
                          ul: ({ node, ...props }) => <ul style={{ marginLeft: '24px', marginTop: '8px', marginBottom: '8px', listStyleType: 'disc' }} {...props} />,
                          ol: ({ node, ...props }) => <ol style={{ marginLeft: '24px', marginTop: '8px', marginBottom: '8px' }} {...props} />,
                          li: ({ node, ...props }) => <li style={{ marginBottom: '6px', lineHeight: '1.6' }} {...props} />,
                          strong: ({ node, ...props }) => <strong style={{ fontWeight: 600, color: 'var(--text-strong)' }} {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote style={{ borderLeft: '3px solid var(--status-success)', paddingLeft: '16px', margin: '12px 0', color: 'var(--text-body)', fontStyle: 'italic' }} {...props} />,
                          table: ({ node, ...props }) => <div style={{ overflowX: 'auto', marginTop: '12px', marginBottom: '12px' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }} {...props} /></div>,
                          thead: ({ node, ...props }) => <thead style={{ backgroundColor: 'var(--surface-page)' }} {...props} />,
                          th: ({ node, ...props }) => <th style={{ padding: '10px 12px', borderBottom: '2px solid var(--border-default)', textAlign: 'left', fontWeight: 600, color: 'var(--text-body)' }} {...props} />,
                          td: ({ node, ...props }) => <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-default)', color: 'var(--text-body)' }} {...props} />,
                          code: ({ node, className, children, ...props }: any) => {
                            const isInline = !className;
                            return isInline ? (
                              <code style={{ background: 'var(--surface-sunken)', padding: '2px 6px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace', color: 'var(--status-danger)' }} {...props}>{children}</code>
                            ) : (
                              <code style={{ display: 'block', background: 'var(--surface-inverse)', color: '#f9fafb', padding: '16px', borderRadius: '8px', fontSize: '13px', fontFamily: 'monospace', overflowX: 'auto', marginTop: '10px', marginBottom: '10px', lineHeight: '1.5' }} {...props}>{children}</code>
                            );
                          },
                          hr: ({ node, ...props }) => <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: '16px 0' }} {...props} />,
                        }}
                      >
                        {queryResponse}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!queryLoading && !queryError && !queryResponse && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px', color: 'var(--text-faint)' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-faint)', marginBottom: '12px', opacity: 0.5 }}>
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 6px 0' }}>Ask a question</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-faint)', margin: 0, maxWidth: '320px', lineHeight: 1.5 }}>
                      Type your question above and press Search to find relevant content from this module's RAGified resources.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      {/* AI Generate Modal (intercept: Generate Now vs Customize with AI chat) */}
      <AiGenerateModal
        isOpen={aiGenerateModal.isOpen}
        onClose={closeAiGenerateModal}
        onGenerateNow={handleAiGenerateNow}
        onGenerateWithInstructions={handleAiGenerateWithInstructions}
        generationType={aiGenerateModal.type}
        contextSummary={aiGenerateModal.context}
        generatedResources={aiGenerateModal.generatedResources}
        isGenerating={aiGenerateModal.isGenerating}
        generationError={aiGenerateModal.generationError}
        onConfirmResources={handleConfirmResources}
        onRegenerateResources={regenerateRejectedResources}
        phaseConfigs={flipClassPhaseConfigs}
        availableTags={availableTags}
        generatedQuestions={aiGenerateModal.generatedQuestions}
        onConfirmQuestions={handleConfirmQuestions}
        onRegenerateQuestions={regenerateRejectedQuestions}
        courseDocumentsAvailable={!!(mod?.selectedTopics && mod.selectedTopics.length > 0)}
        topicIds={mod?.selectedTopics}
      />
    </>
  );
}